import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  inject,
  signal,
  computed,
  HostListener,
  forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { firstValueFrom, Subscription } from 'rxjs';

import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSplitterModule } from 'ng-zorro-antd/splitter';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { Store } from '@ngrx/store';

import { AuthStore } from '@rapider/authentication';
import { ProjectDataService } from '@rapider/project-data';
import {
  NotificationService,
  BrowserStorageManagementService,
} from '@rapider/services';
import {
  DefinitionControllerService,
  CommentControllerService,
  Comment,
} from '@rapider/sdk';
import { environment } from '@rapider/environments';
import { AiChatActions } from '@rapider/ai-chat';
import { RapiderMarkdownEditorComponent } from '@rapider/markdown-editor';
import { MarkdownPreviewComponent } from '@rapider/markdown-preview';

import { DynamicPageRendererComponent } from '../dynamic-page-renderer/dynamic-page-renderer.component';
import { HtmlDefinitionPreviewComponent } from '../html-definition-preview/html-definition-preview.component';
import { HtmlUrlPreviewComponent } from '../html-url-preview/html-url-preview.component';
import {
  AppThemeOption,
  RAPIDER_APP_THEMES,
  normalizeAppThemeKey,
} from '../html-preview/html-preview.component';
import {
  AppConfig,
  PageConfig,
  DYNAMIC_APP_VIEWER_CONFIG,
} from '../dynamic-page-renderer/dynamic-page-renderer.config';
import { ApplicationArtifactsComponent } from '../application-artifacts/application-artifacts.component';
import { E2eTestRunnerService } from '../dynamic-app-viewer/e2e-test-runner.service';
import { isTrustedAppResourceUrl } from '../security/sandbox-security.util';

import {
  UnifiedTaskItem,
  isCoordinateWithinCanvasBounds,
  toggleFilterValue,
} from '../dynamic-app-viewer/dynamic-app-viewer.component';
import { TaskPipelineGuideModalComponent } from '../task-pipeline-guide-modal/task-pipeline-guide-modal.component';

export type AppHarnessTaskItem = UnifiedTaskItem;

@Component({
  selector: 'rapider-app-harness-viewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzSelectModule,
    NzSplitterModule,
    NzModalModule,
    NzButtonModule,
    NzInputModule,
    NzDropDownModule,
    NzTimelineModule,
    NzTagModule,
    NzSwitchModule,
    NzTooltipModule,
    NzMenuModule,
    DynamicPageRendererComponent,
    HtmlDefinitionPreviewComponent,
    HtmlUrlPreviewComponent,
    ApplicationArtifactsComponent,
    RapiderMarkdownEditorComponent,
    MarkdownPreviewComponent,
    TaskPipelineGuideModalComponent,
  ],
  templateUrl: './app-harness-viewer.component.html',
  host: { class: 'block w-full h-full' },
  styles: [
    `
      .selection-pin {
        animation: pulse-pin 1.5s infinite;
      }
      @keyframes pulse-pin {
        0%, 100% { transform: translate(-50%, -50%) scale(1); }
        50% { transform: translate(-50%, -50%) scale(1.15); }
      }
      .comment-hover-target {
        outline: 2px dashed #ef4444 !important;
        outline-offset: -2px;
        cursor: crosshair !important;
      }
      ::ng-deep .comment-hover-target {
        outline: 2px dashed #ef4444 !important;
        outline-offset: -2px;
        cursor: crosshair !important;
      }
      @keyframes task-card-single-shake {
        0%, 100% {
          transform: translateX(0);
        }
        15% {
          transform: translateX(-4px);
        }
        30% {
          transform: translateX(4px);
        }
        50% {
          transform: translateX(-3px);
        }
        70% {
          transform: translateX(2px);
        }
        85% {
          transform: translateX(-1px);
        }
      }
      @keyframes bubble-delayed-shake {
        0%, 65%, 100% {
          transform: scale(1.18) translateX(0) rotate(0deg);
        }
        68% {
          transform: scale(1.18) translateX(-3px) rotate(-1.5deg);
        }
        72% {
          transform: scale(1.18) translateX(3px) rotate(1.5deg);
        }
        76% {
          transform: scale(1.18) translateX(-3px) rotate(-1.2deg);
        }
        80% {
          transform: scale(1.18) translateX(3px) rotate(1.2deg);
        }
        84% {
          transform: scale(1.18) translateX(-1.5px) rotate(-0.6deg);
        }
        88% {
          transform: scale(1.18) translateX(1.5px) rotate(0.6deg);
        }
        92% {
          transform: scale(1.18) translateX(0) rotate(0deg);
        }
      }
      .task-card-updated {
        border-color: #10b981 !important;
        background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 55%, #ffffff 100%) !important;
        box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.35), 0 4px 12px -2px rgba(16, 185, 129, 0.18) !important;
      }
      .task-card-active {
        border-color: #f59e0b !important;
        background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%) !important;
        box-shadow: 0 0 0 2px #f59e0b, 0 6px 16px -2px rgba(245, 158, 11, 0.25) !important;
        animation: task-card-single-shake 0.5s ease-in-out 1;
      }
      .comment-bubble {
        margin-top: -32px;
        transform-origin: bottom left;
      }
      .comment-bubble-active {
        box-shadow: 0 0 0 3px #ffffff, 0 0 0 6px #f59e0b, 0 8px 16px rgba(245, 158, 11, 0.45) !important;
        transform: scale(1.18);
        animation: bubble-delayed-shake 3.5s ease-in-out infinite;
        will-change: transform;
        z-index: 50 !important;
      }
    `,
  ],
})
export class AppHarnessViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly definitionController = inject(DefinitionControllerService);
  private readonly commentController = inject(CommentControllerService);
  private readonly notificationService = inject(NotificationService);
  private readonly browserStorage = inject(BrowserStorageManagementService);
  private readonly store = inject(Store);
  private readonly e2eRunner = inject(E2eTestRunnerService);

  @ViewChild('captureZone') captureZoneRef?: ElementRef<HTMLElement>;
  @ViewChild('canvasWrapper') canvasWrapperRef?: ElementRef<HTMLElement>;

  private boundIframeDoc: Document | null = null;
  private iframePollInterval: any = null;
  private taskSmartPollInterval: any = null;
  private readonly SMART_POLL_INTERVAL_MS = 2 * 60 * 1000;
  private appResizeObserver?: ResizeObserver;
  private scrollTimeout: any = null;
  private isPinningFromModal = false;

  // App & Manifest Signals
  public appConfig = signal<AppConfig | null>(null);
  public appName = signal<string>('App Preview');
  public currentAppId = signal<string | null>(null);
  public installedManifestPages = signal<any[]>([]);
  public activeManifest = signal<any>(null);
  public activePageId = signal<string | null>(null);
  public activePageRoute = signal<string | null>(null);
  public activePageType = signal<string>('AppPage');
  public activePageUrl = signal<string | null>(null);
  public activePageFileName = signal<string>('pageTemplate.html');
  public activeAssetBaseUrl = signal<string>('');
  public activePageConfig = signal<PageConfig | null>(null);
  public currentRouteParams = signal<Record<string, string>>({});
  public currentQueryParams = signal<Record<string, any>>({});

  // UI State Signals
  public isLoading = signal<boolean>(true);
  public error = signal<string | null>(null);
  public configTexts = DYNAMIC_APP_VIEWER_CONFIG.texts;

  // App Harness Viewport, Theme & DevTools Signals
  public viewportMode = signal<'desktop' | 'tablet' | 'mobile'>('desktop');
  public availableThemes = signal<AppThemeOption[]>([...RAPIDER_APP_THEMES]);
  public previewTheme = signal<string>('default');
  public activeThemeOption = computed<AppThemeOption>(() => {
    const key = normalizeAppThemeKey(this.previewTheme());
    return (
      this.availableThemes().find(
        (t) => t.key === key || t.aliases?.includes(key),
      ) || this.availableThemes()[0]
    );
  });
  public isDevToolsOpen = signal<boolean>(false);
  public showGuidanceModal = signal<boolean>(false);
  public apiLogs = signal<any[]>([]);
  public apiLogFilter = signal<
    'all' | 'request' | 'response' | 'error' | 'broadcast'
  >('all');
  public selectedLogItem = signal<any | null>(null);
  public isRecompiling = signal<boolean>(false);
  public recompileStatusText = signal<string>('Recompile Dev');
  public hasDevelopBuild = signal<boolean>(false);
  public developBuildInfo = signal<any>(null);

  /** Computed: Manifest dataModels passed down to enforce entity allowlisting on window.rapiderApi calls. */
  public effectiveDataModels = computed<any[]>(() => {
    const manifest = this.activeManifest();
    const appDef = this.currentAppDef();
    return (
      manifest?.dataModels ||
      appDef?.value?.dataModels ||
      appDef?.dataModels ||
      []
    );
  });

  /**
   * Computed class string for the canvas workspace wrapper.
   * Desktop takes 100% full height with body background.
   * Tablet and Mobile center the device frame in a slate workspace with scrolling.
   */
  public readonly canvasAreaClasses = computed(() => {
    const mode = this.viewportMode();
    if (mode === 'desktop') {
      return 'relative w-full h-full flex-1 flex flex-col min-h-0 overflow-hidden transition-all duration-200 bg-body-background';
    }
    return 'relative w-full h-full flex-1 flex flex-col min-h-0 transition-all duration-200 bg-slate-100 dark:bg-slate-900/60 p-4 overflow-auto items-center';
  });

  /**
   * Computed class string for the device frame.
   * Guarantees 100% height and flex column stretch across all viewports,
   * avoiding Angular NgClass object token deletion bugs when switching modes.
   */
  public readonly deviceFrameClasses = computed(() => {
    const mode = this.viewportMode();
    switch (mode) {
      case 'desktop':
        return 'relative transition-all duration-300 w-full h-full flex-1 min-h-0 flex flex-col';
      case 'tablet':
        return 'relative transition-all duration-300 w-[768px] flex-1 h-full max-h-[1024px] min-h-[640px] my-auto rounded-2xl border-4 border-slate-700 bg-white shadow-2xl overflow-hidden shrink-0 flex flex-col';
      case 'mobile':
        return 'relative transition-all duration-300 w-[375px] flex-1 h-full max-h-[812px] min-h-[550px] my-auto rounded-[36px] border-[6px] border-slate-800 bg-white shadow-2xl overflow-hidden shrink-0 flex flex-col';
    }
  });

  // Rejection Reason Modal Signals
  public isRejectModalVisible = signal<boolean>(false);
  public rejectionReason = signal<string>('');
  public taskPendingRejection = signal<UnifiedTaskItem | null>(null);
  public isSubmittingRejection = signal<boolean>(false);

  public filteredApiLogs = computed(() => {
    const logs = this.apiLogs();
    const filter = this.apiLogFilter();
    if (filter === 'all') return logs;
    if (filter === 'request') return logs.filter((l) => l.type === 'request');
    if (filter === 'response') return logs.filter((l) => l.type === 'success');
    if (filter === 'error') return logs.filter((l) => l.type === 'error');
    if (filter === 'broadcast')
      return logs.filter(
        (l) => l.type === 'broadcast_in' || l.type === 'broadcast_out',
      );
    return logs;
  });

  // Sidebar & Tasks State (Default open on App Builder Preview Harness)
  public isSidebarOpen = signal<boolean>(true);
  public activeSidebarTab = signal<
    'Tasks' | 'Bug Report' | 'Feature Request' | 'Artifacts' | 'E2E Tests'
  >('Tasks');
  public sidebarWidthPx = signal<number>(450);
  public isAddCommentMode = signal<boolean>(false);
  public showBubbles = signal<boolean>(true);
  public selectedTaskId = signal<string | null>(null);
  public bubblePositions = signal<Record<string, { top: number; left: number; isOrphaned?: boolean }>>({});
  public isCanvasScrolling = signal<boolean>(false);

  // Unified App Tasks & Filters
  public comments = signal<Comment[]>([]);
  public featureRequests = signal<any[]>([]);
  public isLoadingComments = signal<boolean>(false);
  public isLoadingMoreComments = signal<boolean>(false);
  public hasMoreComments = signal<boolean>(false);
  public totalCommentsCount = signal<number | null>(null);
  public taskSearchQuery = signal<string>('');
  public taskTypeFilter = signal<string>('ALL');
  public taskStatusFilter = signal<string>('ALL');
  public selectedTaskStatuses = signal<string[]>([]);
  public reportedByMeOnly = signal<boolean>(false);
  public isTaskFilterDropdownOpen = signal<boolean>(false);
  public taskApprovalFilter = signal<'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'>('ALL');
  public taskPageFilter = signal<'CURRENT' | 'ALL'>('CURRENT');

  // Task Inline Edit State (RPDR-613: Editable while Open)
  public isEditingTaskDetail = signal<boolean>(false);
  public editTaskTitle = signal<string>('');
  public editTaskDescription = signal<string>('');
  public editTaskType = signal<'Bug' | 'Feature'>('Bug');
  public isSavingTaskEdit = signal<boolean>(false);

  // Smart 2-Minute Task Polling State (App Builder Preview Harness ONLY)
  public isSmartPollingTasks = signal<boolean>(false);
  public recentlyUpdatedTasks = signal<
    Record<
      string,
      {
        updatedAt: number;
        statusChanged: boolean;
        previousStatus?: string;
        newStatus?: string;
        hasNewReplies: boolean;
        newRepliesCount: number;
      }
    >
  >({});
  public recentlyUpdatedTasksCount = computed<number>(
    () => Object.keys(this.recentlyUpdatedTasks()).length,
  );

  public readonly taskStatusFilterMeta: ReadonlyArray<{
    value: string;
    label: string;
    iconClass: string;
    iconColorClass: string;
  }> = [
    { value: 'Open', label: 'Open', iconClass: 'fa-regular fa-circle-dot', iconColorClass: 'text-gray-500' },
    { value: 'Accepted', label: 'Accepted', iconClass: 'fa-solid fa-check', iconColorClass: 'text-blue-500' },
    { value: 'In Progress', label: 'In Progress', iconClass: 'fa-solid fa-clock', iconColorClass: 'text-sky-500' },
    { value: 'In Review', label: 'In Review', iconClass: 'fa-solid fa-clipboard-check', iconColorClass: 'text-purple-500' },
    { value: 'Ready for Release', label: 'Ready for Release', iconClass: 'fa-solid fa-tag', iconColorClass: 'text-amber-500' },
    { value: 'Released', label: 'Released', iconClass: 'fa-solid fa-rocket', iconColorClass: 'text-blue-600' },
    { value: 'Confirmed', label: 'Confirmed', iconClass: 'fa-solid fa-circle-check', iconColorClass: 'text-emerald-500' },
    { value: 'Reopened', label: 'Reopened', iconClass: 'fa-solid fa-rotate-left', iconColorClass: 'text-rose-500' },
    { value: 'Closed', label: 'Closed', iconClass: 'fa-solid fa-circle-xmark', iconColorClass: 'text-zinc-500' },
  ];

  public activeDropdownFilterCount = computed<number>(() => {
    const statusCount =
      this.selectedTaskStatuses().length > 0
        ? this.selectedTaskStatuses().length
        : this.taskStatusFilter() !== 'ALL'
          ? 1
          : 0;
    return (this.reportedByMeOnly() ? 1 : 0) + statusCount;
  });

  public setTaskPageFilter(filter: 'CURRENT' | 'ALL'): void {
    this.taskPageFilter.set(filter);
    this.loadedTasksLimit.set(20);
  }

  public clearPageFilter(): void {
    this.taskPageFilter.set('CURRENT');
    this.loadedTasksLimit.set(20);
  }
  public loadedTasksLimit = signal<number>(20);
  public selectedTaskForDetail = signal<UnifiedTaskItem | null>(null);
  public taskToDelete = signal<UnifiedTaskItem | null>(null);
  public taskReplies = signal<Comment[]>([]);
  public isLoadingReplies = signal<boolean>(false);
  public replyContent = signal<string>('');
  public isSubmittingReply = signal<boolean>(false);
  public isSubmittingTask = signal<boolean>(false);
  public draftTaskType = signal<'Bug' | 'Feature'>('Bug');
  public draftTaskTitle = signal<string>('');
  public draftTaskDescription = signal<string>('');
  public isCreateTaskModalVisible = signal<boolean>(false);
  public pinnedMetadata = signal<{
    elementId?: string;
    uniqueSelector?: string;
    offsetX?: number;
    offsetY?: number;
    absoluteX?: number;
    absoluteY?: number;
    pageId?: string;
    tag?: string;
    x?: number;
    y?: number;
  } | null>(null);
  public currentAppDef = signal<any | null>(null);
  private loadedHarnessAppId: string | null = null;
  private loadedHarnessManifestUrl: string | null = null;
  private tasksLoadedForAppId: string | null = null;
  public expandedTaskDescriptions = signal<Record<string, boolean>>({});

  public readonly commentsPageSize = 20;

  public readonly taskStatusOptions: readonly string[] = [
    'Open',
    'Accepted',
    'In Progress',
    'In Review',
    'Ready for Release',
    'Released',
    'Confirmed',
    'Reopened',
    'Closed',
  ];

  /**
   * Returns Tailwind CSS classes for the unified status badge matching the pipeline picture.
   */
  public getTaskStatusClass(status?: string): Record<string, boolean> {
    const s = this.getNormalizedTaskStatus({ status } as any);
    return {
      'bg-gray-100 text-gray-700 border-gray-200': s === 'Open',
      'bg-blue-50 text-blue-700 border-blue-200': s === 'Accepted',
      'bg-sky-50 text-sky-700 border-sky-200': s === 'In Progress',
      'bg-purple-50 text-purple-700 border-purple-200': s === 'In Review',
      'bg-amber-50 text-amber-700 border-amber-200': s === 'Ready for Release',
      'bg-blue-100 text-blue-800 border-blue-300': s === 'Released',
      'bg-emerald-50 text-emerald-700 border-emerald-200': s === 'Confirmed',
      'bg-rose-50 text-rose-700 border-rose-200': s === 'Reopened',
      'bg-zinc-100 text-zinc-700 border-zinc-300': s === 'Closed',
    };
  }

  /**
   * Returns dot/indicator background color class for active filter tags.
   */
  public getTaskStatusDotClass(status?: string): Record<string, boolean> {
    const s = this.getNormalizedTaskStatus({ status } as any);
    return {
      'bg-gray-500': s === 'Open',
      'bg-blue-500': s === 'Accepted',
      'bg-sky-500': s === 'In Progress',
      'bg-purple-500': s === 'In Review',
      'bg-amber-500': s === 'Ready for Release',
      'bg-blue-600': s === 'Released',
      'bg-emerald-500': s === 'Confirmed',
      'bg-rose-500': s === 'Reopened',
      'bg-zinc-500': s === 'Closed',
    };
  }

  /**
   * Returns normalized display label for status.
   */
  public getDisplayStatus(status?: string): string {
    return this.getNormalizedTaskStatus({ status } as any);
  }

  // E2E Test State
  public testMode = signal<boolean>(false);
  public virtualMouse = signal<boolean>(true);
  public captureScreenshots = signal<boolean>(false);
  public captureScreenshotsOnFailure = signal<boolean>(false);
  public recordVideo = signal<boolean>(false);
  public testActivePageOnly = signal<boolean>(false);
  public isTestRunning = signal<boolean>(false);
  public isSettingsCollapsed = signal<boolean>(false);
  public testLogs = signal<
    {
      type: 'system' | 'suite' | 'test';
      message?: string;
      suiteName?: string;
      stepName?: string;
      stepNumber?: number;
      explanation?: string;
      action?: string;
      success?: boolean | null;
      timestamp: number;
      error?: string;
      screenshot?: string | null;
    }[]
  >([]);
  public selectedScreenshot = signal<string | null>(null);
  public isScreenshotModalVisible = signal<boolean>(false);
  public isReportPreviewVisible = signal<boolean>(false);
  public selectedReportContent = signal<string>('');

  // Edit Approval Modal Signals
  public isEditApprovalModalVisible = signal<boolean>(false);
  public editingApprovalTask = signal<UnifiedTaskItem | null>(null);
  public editingApprovalValue = signal<'approved' | 'rejected'>('approved');
  public editingApprovalInitialValue = signal<'approved' | 'rejected'>('approved');

  // QA Audit State
  public isQaAuditRunning = signal<boolean>(false);
  public qaAuditData = signal<any | null>(null);
  public qaAuditError = signal<string | null>(null);
  public activeQaTab = signal<'audit' | 'e2e'>('e2e');
  public expandedAuditPage = signal<string | null>(null);

  // AI Quick Actions
  public aiActions = signal<any[]>([]);

  private paramSub?: Subscription;

  public canApproveTasks = computed(() => {
    const person = this.authStore.activePerson();
    const user = this.authStore.user();
    if (!person && !user) return false;

    const currentPersonId = person?.id;
    const currentUserId = user?.id;
    const appDef = this.currentAppDef();
    const appCreatedById =
      appDef?.createdById ||
      appDef?.createdByPersonId ||
      appDef?.createdBy ||
      appDef?.value?.createdById ||
      appDef?.value?.createdByPersonId ||
      appDef?.value?.createdBy;

    if (appCreatedById && (appCreatedById === currentPersonId || appCreatedById === currentUserId)) {
      return true;
    }

    const storePermissions: string[] = this.authStore.permissions() || [];
    const userPermissions: string[] = (user as any)?.permissions || [];
    const userScope: string[] = (user as any)?.scope || [];
    const userRoles = (user as any)?.roles || [];

    const allAuthStrings = [
      ...storePermissions,
      ...userPermissions,
      ...userScope,
      ...(Array.isArray(userRoles) ? userRoles.map((r: any) => (typeof r === 'string' ? r : r?.name || '')) : []),
    ].map((s) => (typeof s === 'string' ? s.trim().toLowerCase() : ''));

    const isAdmin =
      allAuthStrings.includes('admin') ||
      allAuthStrings.includes('superadmin') ||
      allAuthStrings.includes('administrator');

    const hasApprovePermission =
      allAuthStrings.includes('app:approve-request') ||
      allAuthStrings.includes('approveapprequest') ||
      isAdmin;

    return hasApprovePermission;
  });

  public hasActiveFilters = computed<boolean>(() => {
    return (
      !!this.taskSearchQuery().trim() ||
      this.taskTypeFilter() !== 'ALL' ||
      this.selectedTaskStatuses().length > 0 ||
      this.taskStatusFilter() !== 'ALL' ||
      this.reportedByMeOnly() ||
      this.taskPageFilter() !== 'CURRENT'
    );
  });

  private extractTaskTitleAndDescription(
    rawContent: string,
    metaTitle?: string,
    metaDescription?: string,
    defaultTitle: string = 'Task',
  ): { title: string; description: string } {
    const content = (rawContent || '').trim();

    // 1. If explicit metadata exists for description, use it directly
    if (metaDescription !== undefined && metaDescription !== null && metaDescription.trim() !== '') {
      const explicitTitle = metaTitle?.trim();
      const firstLine = content.split('\n')[0]?.replace(/^#+\s*/, '').trim() || '';
      return {
        title: explicitTitle || firstLine || defaultTitle,
        description: metaDescription.trim(),
      };
    }

    const lines = content.split('\n');
    const rawFirstLine = lines[0]?.trim() || '';
    const cleanFirstLine = rawFirstLine.replace(/^#+\s*/, '').trim();

    // Explicit title from metadata takes precedence; otherwise use clean first line
    const title = metaTitle?.trim() || cleanFirstLine || defaultTitle;

    // 2. Extract description cleanly without duplicating the title
    let description = '';

    // If explicit title exists and content does NOT start with that title,
    // then the content is already a distinct description
    if (metaTitle?.trim() && !content.toLowerCase().startsWith(metaTitle.trim().toLowerCase())) {
      description = content;
    } else if (metaTitle?.trim() && content.toLowerCase().startsWith(metaTitle.trim().toLowerCase())) {
      // Content begins with the title -> strip the title
      description = content.slice(metaTitle.trim().length).replace(/^\s*\n+/, '').trim();
    } else if (lines.length > 1) {
      // Multi-line content where the first line was used as the title
      let remainingLines = lines.slice(1);
      // If second line was a markdown setext heading underline (=== or ---), strip it
      if (remainingLines.length > 0 && /^[=-]{2,}\s*$/.test(remainingLines[0].trim())) {
        remainingLines = remainingLines.slice(1);
      }
      const remainder = remainingLines.join('\n').replace(/^\s*\n+/, '').trim();
      // If the remainder is identical to the title, do not duplicate it
      if (remainder.toLowerCase() !== title.toLowerCase()) {
        description = remainder;
      }
    } else {
      // Single line content where the whole text is already the title
      description = '';
    }

    // Final safety check: if description is identical to title, clear description
    if (description.toLowerCase() === title.toLowerCase()) {
      description = '';
    }

    return { title, description };
  }

  public unifiedTasks = computed<UnifiedTaskItem[]>(() => {
    const commentsTasks: UnifiedTaskItem[] = (this.comments() || [])
      .filter((c) => !c.parentCommentId)
      .map(
        (c) => {
        const rawContentStr = String(c.content || '');
        const hasInlineTypePrefix = /^\[(Feature|Bug)\]\s+/i.test(rawContentStr);
        const inlinePrefixMatch = rawContentStr.match(/^\[(Feature|Bug)\]\s+/i);
        const isFeature = inlinePrefixMatch
          ? inlinePrefixMatch[1].toLowerCase() === 'feature'
          : c.category === 'Feature Request' ||
            c.category === 'Feature' ||
            c.category === 'New Feature';
        const taskType: 'Bug' | 'Feature' = isFeature ? 'Feature' : 'Bug';
        const cleanedContentStr = hasInlineTypePrefix
          ? rawContentStr.replace(/^\[(Feature|Bug)\]\s+/i, '')
          : rawContentStr;
        const meta = this.parseTaskMetadata({
          type: taskType,
          rawItem: c,
        } as any);
        const coords = this.parseCommentCoordinates(c);
        const avatarUrl = this.extractAvatarUrl(c);

        const parsed = this.extractTaskTitleAndDescription(
          cleanedContentStr,
          hasInlineTypePrefix ? undefined : meta?.title,
          hasInlineTypePrefix ? undefined : meta?.description,
          isFeature ? 'Feature Request' : 'Bug Report',
        );

        const taskPageId =
          meta?.pageId ||
          meta?.page ||
          meta?.route ||
          meta?.pageKey ||
          coords?.pageId ||
          (c as any)?.pageId ||
          (c as any)?.route;

        return {
          id: '',
          rawId: c.id || '',
          title: parsed.title,
          description: parsed.description,
          type: taskType,
          status: c.status || 'Open',
          isApproved: (c as any).isApproved ?? null,
          createdBy: this.formatAuthorName(c.createdBy),
          createdById:
            (c as any).createdById ||
            (c as any).createdByPersonId ||
            (c as any).userId ||
            (c as any).authorId ||
            (c as any).createdBy,
          authorAvatarUrl: avatarUrl,
          createdAt: c.createdDate || new Date(),
          replyCount: (c as any).replyCount ?? (c as any).repliesCount ?? 0,
          lastReplierNames: (c as any).lastReplierNames ?? (c as any).lastRepliers ?? [],
          elementSelector: meta?.uniqueSelector || meta?.elementId || undefined,
          elementId: meta?.elementId || undefined,
          tag: meta?.tag || undefined,
          bubbleMetadata: coords || undefined,
          coordinates: coords || undefined,
          pageId: taskPageId || undefined,
          rawItem: c,
        };
      },
    );

    const features: UnifiedTaskItem[] = (this.featureRequests() || []).map(
      (r) => {
        const meta =
          this.parseTaskMetadata({
            type: 'Feature',
            rawItem: r,
          } as any) || {};
        const coords = this.parseRequestCoordinates(r);
        let status = r.status || 'Open';
        if (status === 'Requested') status = 'Open';
        else if (status === 'Completed') status = 'Resolved';
        else if (status === 'Declined') status = 'Closed';
        const avatarUrl = this.extractAvatarUrl(r);

        const parsed = this.extractTaskTitleAndDescription(
          r.description || '',
          r.title || meta?.title,
          meta?.description,
          'Feature Request',
        );

        const taskPageId =
          meta?.pageId ||
          meta?.page ||
          meta?.route ||
          meta?.pageKey ||
          coords?.pageId ||
          (r as any)?.pageId ||
          (r as any)?.route;

        return {
          id: '',
          rawId: r.id || '',
          title: parsed.title,
          description: parsed.description,
          type: 'Feature',
          rawType: r.type,
          status: status,
          isApproved: (r as any).isApproved ?? null,
          createdBy: this.formatAuthorName(r.authorName),
          createdById:
            (r as any).createdById ||
            (r as any).createdByPersonId ||
            (r as any).userId ||
            (r as any).authorId ||
            (r as any).createdBy,
          authorAvatarUrl: avatarUrl,
          createdAt: r.createdAt || r.createdDate || new Date(),
          elementSelector: meta?.uniqueSelector || meta?.elementId || undefined,
          elementId: meta?.elementId || undefined,
          tag: meta?.tag || undefined,
          bubbleMetadata: coords || undefined,
          coordinates: coords || undefined,
          pageId: taskPageId || undefined,
          rawItem: r,
        };
      },
    );

    const combined = [...commentsTasks, ...features].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const total = combined.length;
    return combined.map((task, index) => ({
      ...task,
      id: `TSK-${101 + (total - 1 - index)}`,
      bubbleNumber: total - index,
    }));
  });

  /**
   * Formats the sequential bubble number so it always fits inside the bubble badge:
   * - 1..999: numeric string ("1" .. "999")
   * - 1000..3599: 1 letter + 2 digits ("A00" .. "Z99", where A00 = 1000, A99 = 1099, B00 = 1100, ..., Z99 = 3599)
   * - 3600..10359: 2 letters + 1 digit ("AA0" .. "ZZ9")
   * - 10360+: base-36 uppercase string
   */
  public formatBubbleNumber(num?: number | null): string {
    if (num === undefined || num === null || isNaN(num)) return '';
    const n = Math.max(0, Math.floor(num));
    if (n <= 999) {
      return String(n);
    }
    if (n <= 3599) {
      const offset = n - 1000;
      const letter = String.fromCharCode(65 + Math.floor(offset / 100));
      const digits = String(offset % 100).padStart(2, '0');
      return `${letter}${digits}`;
    }
    if (n <= 10359) {
      const offset = n - 3600;
      const pairIndex = Math.floor(offset / 10);
      const firstLetter = String.fromCharCode(65 + Math.floor(pairIndex / 26));
      const secondLetter = String.fromCharCode(65 + (pairIndex % 26));
      const digit = offset % 10;
      return `${firstLetter}${secondLetter}${digit}`;
    }
    return n.toString(36).toUpperCase();
  }

  /**
   * Resolves the task's recorded page identifier from direct property, coordinates, or metadata.
   */
  public getTaskPageIdentifier(task: UnifiedTaskItem | any): string | undefined {
    if (!task) return undefined;
    if (task.pageId) return task.pageId;
    if (task.coordinates?.pageId) return task.coordinates.pageId;
    if (task.bubbleMetadata?.pageId) return task.bubbleMetadata.pageId;

    const raw = task.rawItem || task;
    if (raw) {
      if (raw.pageId) return raw.pageId;
      if (raw.route) return raw.route;
      if (raw.page) return raw.page;
      if (raw.pageKey) return raw.pageKey;

      let rawMeta = raw.metadata;
      if (typeof rawMeta === 'string') {
        try {
          rawMeta = JSON.parse(rawMeta);
        } catch {}
      }
      if (Array.isArray(rawMeta)) {
        const found = rawMeta.find(
          (m: any) =>
            m &&
            (m.key === 'pageId' ||
              m.key === 'page' ||
              m.key === 'route' ||
              m.key === 'pageKey'),
        );
        if (found && found.value) return String(found.value);
      } else if (typeof rawMeta === 'object' && rawMeta !== null) {
        const val =
          rawMeta.pageId || rawMeta.page || rawMeta.route || rawMeta.pageKey;
        if (val) return String(val);
      }
    }
    return undefined;
  }

  /**
   * Finds the manifest definition matching the currently active page.
   */
  public getActivePageDefinition(): any | null {
    const activeRoute = this.activePageRoute() || this.activePageId();
    const pages = this.installedManifestPages() || [];
    if (!activeRoute) return pages[0] || null;

    const clean = activeRoute.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    return (
      pages.find((p: any) => {
        const pRoute = (p.route || '').toLowerCase().replace(/^\/+|\/+$/g, '');
        const pKey = (p.key || '').toLowerCase();
        const pId = (p.id || '').toLowerCase();
        const pName = (p.name || p.title || '').toLowerCase();
        return (
          pRoute === clean ||
          pKey === clean ||
          pId === clean ||
          pName === clean ||
          pRoute === `${clean}/:id` ||
          `${pRoute}/:id` === clean
        );
      }) || null
    );
  }

  /**
   * Computes all normalized tokens (route, key, id, name, htmlFilePath) for the active page.
   */
  public getActivePageTokens(): Set<string> {
    const tokens = new Set<string>();
    const addToken = (val: string | null | undefined) => {
      if (!val) return;
      const s = String(val).trim().toLowerCase();
      if (!s) return;
      tokens.add(s);
      const stripped = s.replace(/^\/+|\/+$/g, '');
      if (stripped) {
        tokens.add(stripped);
        const noParams = stripped.replace(/\/:(id|action|key).*$/i, '');
        if (noParams) tokens.add(noParams);
        const basename = stripped.split('/').pop()?.replace(/\.html$/i, '');
        if (basename) tokens.add(basename);
      }
    };

    addToken(this.activePageId());
    addToken(this.activePageRoute());
    addToken(this.activePageFileName());
    addToken(this.route.snapshot.paramMap.get('pageKey'));

    const activePageDef = this.getActivePageDefinition();
    if (activePageDef) {
      addToken(activePageDef.id);
      addToken(activePageDef.route);
      addToken(activePageDef.key);
      addToken(activePageDef.name);
      addToken(activePageDef.title);
      addToken(activePageDef.htmlFilePath);
    }

    return tokens;
  }

  /**
   * Determines whether a given task or bug belongs to the active page.
   */
  public isTaskOnActivePage(task: UnifiedTaskItem | any): boolean {
    const taskPage = this.getTaskPageIdentifier(task);
    if (!taskPage || !taskPage.trim()) {
      return false;
    }

    const tokens = this.getActivePageTokens();
    const rawPage = taskPage.trim().toLowerCase();
    const strippedPage = rawPage.replace(/^\/+|\/+$/g, '');
    const noParamsPage = strippedPage.replace(/\/:(id|action|key).*$/i, '');
    const basenamePage = strippedPage.split('/').pop()?.replace(/\.html$/i, '') || '';

    return (
      tokens.has(rawPage) ||
      tokens.has(strippedPage) ||
      tokens.has(noParamsPage) ||
      (basenamePage !== '' && tokens.has(basenamePage))
    );
  }

  /**
   * Returns a display friendly page name for a task.
   */
  public getTaskPageName(task: UnifiedTaskItem): string {
    const pageIdent = this.getTaskPageIdentifier(task);
    if (!pageIdent) return 'General';
    const clean = pageIdent.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    const pages = this.installedManifestPages() || [];
    const found = pages.find((p: any) => {
      const pRoute = (p.route || '').toLowerCase().replace(/^\/+|\/+$/g, '');
      const pKey = (p.key || '').toLowerCase();
      const pId = (p.id || '').toLowerCase();
      const pName = (p.name || p.title || '').toLowerCase();
      return (
        pRoute === clean ||
        pKey === clean ||
        pId === clean ||
        pName === clean ||
        pRoute === `${clean}/:id`
      );
    });
    return found?.name || found?.title || found?.route || pageIdent;
  }

  public filteredTasks = computed<UnifiedTaskItem[]>(() => {
    const query = this.taskSearchQuery().toLowerCase().trim();
    const typeFilter = this.taskTypeFilter();
    const statusFilter = this.taskStatusFilter();
    const selectedStatuses = this.selectedTaskStatuses();
    const onlyMine = this.reportedByMeOnly();
    const pageFilter = this.taskPageFilter();

    return this.unifiedTasks().filter((task) => {
      // Filter by active page when in CURRENT page mode
      if (pageFilter === 'CURRENT' && !this.isTaskOnActivePage(task)) {
        return false;
      }

      if (onlyMine && !this.isTaskReporter(task)) {
        return false;
      }

      const formattedBubble = this.formatBubbleNumber(task.bubbleNumber).toLowerCase();
      const rawBubble = String(task.bubbleNumber ?? '');
      const matchesSearch =
        !query ||
        task.title.toLowerCase().includes(query) ||
        task.id.toLowerCase().includes(query) ||
        formattedBubble.includes(query) ||
        rawBubble.includes(query) ||
        task.description.toLowerCase().includes(query) ||
        task.createdBy.toLowerCase().includes(query);

      const matchesType =
        typeFilter === 'ALL' || task.type === typeFilter;

      const normStatus = this.getNormalizedTaskStatus(task);
      const matchesStatus =
        selectedStatuses.length > 0
          ? selectedStatuses.includes(normStatus)
          : statusFilter === 'ALL' ||
            task.status === statusFilter ||
            normStatus === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  });

  public getNormalizedTaskStatus(task: UnifiedTaskItem | null): string {
    if (!task) return 'Open';
    const rawStatus = (task.status || '').trim();
    if (rawStatus === 'Reopened' || rawStatus === 'reopened' || rawStatus === 'Reporter Rejected' || rawStatus === 'reporter_rejected') {
      return 'Reopened';
    }
    if (rawStatus === 'Confirmed' || rawStatus === 'confirmed' || rawStatus === 'Resolved' || rawStatus === 'resolved' || rawStatus === 'Completed') {
      return 'Confirmed';
    }
    if (rawStatus === 'Released' || rawStatus === 'released') {
      return 'Released';
    }
    if (rawStatus === 'Ready for Release' || rawStatus === 'ready_for_release') {
      return 'Ready for Release';
    }
    if (rawStatus === 'In Review' || rawStatus === 'in_review' || rawStatus === 'Ready to Test' || rawStatus === 'ready_to_test') {
      return 'In Review';
    }
    if (rawStatus === 'In Progress' || rawStatus === 'in_progress') {
      return 'In Progress';
    }
    if (rawStatus === 'Accepted' || rawStatus === 'accepted' || rawStatus === 'Approved' || rawStatus === 'approved') {
      return 'Accepted';
    }
    if (rawStatus === 'Closed' || rawStatus === 'closed' || rawStatus === 'Declined' || task.isApproved === false) {
      return 'Closed';
    }
    if (task.isApproved === true) {
      return 'Accepted';
    }
    return 'Open';
  }

  public displayedTasks = computed<UnifiedTaskItem[]>(() => {
    return this.filteredTasks();
  });

  private baseTasksByReporter = computed<UnifiedTaskItem[]>(() => {
    const all = this.unifiedTasks();
    return this.reportedByMeOnly()
      ? all.filter((t) => this.isTaskReporter(t))
      : all;
  });

  public activePageTasksCount = computed<number>(() => {
    return this.baseTasksByReporter().filter((t) => this.isTaskOnActivePage(t)).length;
  });

  public totalAppTasksCount = computed<number>(() => {
    if (this.reportedByMeOnly()) {
      return this.baseTasksByReporter().length;
    }
    const serverTotal = this.totalCommentsCount();
    const loadedTotal = this.unifiedTasks().length;
    return serverTotal !== null && serverTotal > loadedTotal
      ? serverTotal
      : loadedTotal;
  });

  public totalTasksCount = computed(() => {
    if (this.taskPageFilter() === 'ALL') {
      return this.totalAppTasksCount();
    }
    return this.activePageTasksCount();
  });

  public bugTasksCount = computed(() => {
    const tasks =
      this.taskPageFilter() === 'ALL'
        ? this.baseTasksByReporter()
        : this.baseTasksByReporter().filter((t) => this.isTaskOnActivePage(t));
    return tasks.filter((t) => t.type === 'Bug').length;
  });

  public featureTasksCount = computed(() => {
    const tasks =
      this.taskPageFilter() === 'ALL'
        ? this.baseTasksByReporter()
        : this.baseTasksByReporter().filter((t) => this.isTaskOnActivePage(t));
    return tasks.filter((t) => t.type === 'Feature').length;
  });

  ngOnInit(): void {
    this.loadThemeRegistry();
    this.startSmartTaskPolling();
    this.paramSub = this.route.paramMap.subscribe((params) => {
      const appId = params.get('appId');
      const pageKey = params.get('pageKey');
      const action = params.get('action');
      const entityId = params.get('entityId');

      if (!appId) {
        this.error.set(this.configTexts.noAppId);
        this.isLoading.set(false);
        return;
      }

      this.currentAppId.set(appId);

      const routeParams: Record<string, string> = {};
      if (entityId) routeParams['id'] = entityId;
      if (action) routeParams['action'] = action;
      if (pageKey) routeParams['key'] = pageKey;
      this.currentRouteParams.set(routeParams);

      let projectId: string = this.authStore.activeProject()?.id || '';
      if (!projectId) {
        projectId =
          this.browserStorage.getItem('projectId') ||
          this.browserStorage.getItem('activeProjectId') ||
          (this.authStore.user() as any)?.['lastProjectId'] ||
          '';
      }
      this.loadHarnessApp(appId, projectId, pageKey, action);
    });

    this.route.queryParams.subscribe((queryParams) => {
      this.currentQueryParams.set(queryParams || {});
    });
  }

  private async loadThemeRegistry(): Promise<void> {
    try {
      const registry: any = await firstValueFrom(
        this.httpClient.get('/assets/themes/themes.json'),
      );
      if (
        registry &&
        Array.isArray(registry.themes) &&
        registry.themes.length > 0
      ) {
        this.availableThemes.set(registry.themes);
      }
    } catch {
      // Fallback to built-in RAPIDER_APP_THEMES
    }
  }

  public selectPreviewTheme(themeKey: string): void {
    const normalized = normalizeAppThemeKey(themeKey);
    this.previewTheme.set(normalized);
    const appKey =
      this.currentAppDef()?.key ||
      this.currentAppId() ||
      'global';
    try {
      this.browserStorage.setItem(
        `rappider_harness_theme_${appKey}`,
        normalized,
      );
    } catch {
      // Ignore storage write errors
    }
  }

  private initializeHarnessAppTheme(
    appId: string,
    projectId: string,
    appDef?: any,
  ): void {
    const appKey = appDef?.key || appId;
    let storedTheme = '';
    try {
      storedTheme =
        this.browserStorage.getItem(`rappider_harness_theme_${appKey}`) ||
        this.browserStorage.getItem(`rappider_harness_theme_${appId}`) ||
        '';
    } catch {
      // Ignore storage read errors
    }

    if (projectId) {
      this.projectDataService.getProjectApplications(projectId).subscribe({
        next: (apps: any[]) => {
          const matched = (apps || []).find(
            (a: any) =>
              !a.isDeleted &&
              (a.applicationKey === appId ||
                a.applicationDefinitionId === appId ||
                (appDef?.id && a.applicationDefinitionId === appDef.id) ||
                (appDef?.key && a.applicationKey === appDef.key)),
          );
          const workspaceTheme = matched?.theme ?? matched?.value?.theme;
          const resolved = normalizeAppThemeKey(
            storedTheme ||
              workspaceTheme ||
              appDef?.value?.theme ||
              appDef?.theme ||
              'default',
          );
          this.previewTheme.set(resolved);
        },
        error: () => {
          this.previewTheme.set(
            normalizeAppThemeKey(
              storedTheme || appDef?.value?.theme || appDef?.theme || 'default',
            ),
          );
        },
      });
    } else {
      this.previewTheme.set(
        normalizeAppThemeKey(
          storedTheme || appDef?.value?.theme || appDef?.theme || 'default',
        ),
      );
    }
  }

  ngAfterViewInit(): void {
    // Listen to iframe postMessages for live API event capture
    window.addEventListener('message', this.onWindowMessage);
    this.setupIframeListeners();
    if (this.captureZoneRef?.nativeElement) {
      this.captureZoneRef.nativeElement.addEventListener('click', this.boundHandleClick, true);
      this.captureZoneRef.nativeElement.addEventListener('mousedown', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.addEventListener('pointerdown', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.addEventListener('mouseup', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.addEventListener('pointerup', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.addEventListener('submit', this.boundHandlePointerEvent, true);
    }
    if (typeof ResizeObserver !== 'undefined' && this.elementRef?.nativeElement) {
      this.appResizeObserver = new ResizeObserver(() => {
        this.recalculateBubblePositions();
      });
      this.appResizeObserver.observe(this.elementRef.nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.paramSub?.unsubscribe();
    window.removeEventListener('message', this.onWindowMessage);
    if (this.captureZoneRef?.nativeElement) {
      this.captureZoneRef.nativeElement.removeEventListener('click', this.boundHandleClick, true);
      this.captureZoneRef.nativeElement.removeEventListener('mousedown', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.removeEventListener('pointerdown', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.removeEventListener('mouseup', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.removeEventListener('pointerup', this.boundHandlePointerEvent, true);
      this.captureZoneRef.nativeElement.removeEventListener('submit', this.boundHandlePointerEvent, true);
    }
    if (this.iframePollInterval) {
      clearInterval(this.iframePollInterval);
      this.iframePollInterval = null;
    }
    this.stopSmartTaskPolling();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    this.removeIframeListeners();
    if (this.appResizeObserver) {
      this.appResizeObserver.disconnect();
    }
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.recalculateBubblePositions();
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    this.triggerScrollHiding();
  }

  private triggerScrollHiding(): void {
    if (!this.isCanvasScrolling()) {
      this.isCanvasScrolling.set(true);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.recalculateBubblePositions();
      this.isCanvasScrolling.set(false);
    }, 150);
  }

  private boundHandlePointerEvent = (e: Event) => {
    if (
      this.isAddCommentMode() ||
      (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive)
    ) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  };

  private boundHandleClick = (e: MouseEvent) => {
    if (
      this.isAddCommentMode() ||
      (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive)
    ) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.handleCaptureZoneClick(e);
    }
  };

  private boundHandleMouseMove = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      this.handleCaptureZoneMouseMove(e);
    }
  };

  private boundHandleMouseOut = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      this.handleCaptureZoneMouseOut(e);
    }
  };

  private boundHandleScroll = (_e: Event) => {
    this.triggerScrollHiding();
  };

  private boundHandleWheel = (_e: Event) => {
    this.triggerScrollHiding();
  };

  private boundHandleTouchMove = (_e: Event) => {
    this.triggerScrollHiding();
  };

  private onWindowMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data) return;

    if (data.type === 'RAPIDER_UI_ACTION') {
      if (
        data.action?.type === 'navigate' &&
        (this.isAddCommentMode() ||
          (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive))
      ) {
        return; // Drop navigation during bug / feature bubble dropping
      }
    }

    if (data.type === 'RAPIDER_API_REQUEST' || data.type === 'RAPIDER_DATA_MANAGER_CALL') {
      this.handleApiEvent({
        type: 'request',
        reqId: data.reqId,
        operation: data.operation || data.action,
        entityName: data.entityName,
        payload: data.payload,
        timestamp: new Date(),
      });
    } else if (data.type === 'RAPIDER_API_RESPONSE') {
      this.handleApiEvent({
        type: 'success',
        reqId: data.reqId,
        response: data.payload,
        timestamp: new Date(),
      });
    } else if (data.type === 'RAPIDER_API_ERROR') {
      this.handleApiEvent({
        type: 'error',
        reqId: data.reqId,
        error: data.error,
        timestamp: new Date(),
      });
    } else if (data.type === 'RAPIDER_BROADCAST') {
      this.handleApiEvent({
        type: 'broadcast_in',
        eventName: data.eventName,
        payload: data.payload,
        timestamp: new Date(),
      });
    }
  };

  /**
   * Loads the application in development / harness mode.
   * Checks for a compiled develop build on S3 first; falls back to definition preview.
   */
  private loadHarnessApp(
    appId: string,
    projectId: string,
    pageKey: string | null,
    action: string | null = null,
  ): void {
    const isSameApp = this.loadedHarnessAppId === appId && !!this.currentAppDef();
    this.error.set(null);

    if (!isSameApp) {
      this.isLoading.set(true);
      this.loadedHarnessAppId = null;
      this.loadedHarnessManifestUrl = null;
      this.tasksLoadedForAppId = null;
      this.activeManifest.set(null);
      this.comments.set([]);
      this.featureRequests.set([]);
      this.totalCommentsCount.set(null);
      this.hasMoreComments.set(false);
      this.recentlyUpdatedTasks.set({});
    }

    // Fast path for intra-app page navigation: reuse already-loaded manifest or definition
    if (isSameApp) {
      const cachedDef = this.currentAppDef();
      if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
        this.ensureAppTasksLoaded();
      }
      if (this.activeManifest()) {
        this.processManifest(
          this.activeManifest(),
          appId,
          pageKey,
          action,
          this.loadedHarnessManifestUrl,
        );
        if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
          setTimeout(() => this.recalculateBubblePositions(), 200);
        }
        return;
      }

      if (cachedDef?.value?.developBuild?.manifestUrl) {
        const rawUrl = String(
          cachedDef.value.developBuild.rawManifestUrl ||
            cachedDef.value.developBuild.manifestUrl,
        ).split('?')[0];
        const devManifestUrl = `${rawUrl}?b=${cachedDef.value.developBuild.buildNumber || 'dev'}`;
        if (isTrustedAppResourceUrl(devManifestUrl)) {
          this.loadedHarnessManifestUrl = devManifestUrl;
          this.httpClient.get<any>(devManifestUrl).subscribe({
            next: (manifest) => {
              this.processManifest(
                manifest,
                appId,
                pageKey,
                action,
                devManifestUrl,
              );
            },
            error: () => {
              const fallbackPages =
                cachedDef?.value?.webPages ||
                cachedDef?.webPages ||
                cachedDef?.value?.pages ||
                cachedDef?.pages ||
                [];
              this.loadDirectDefinitionPage(
                cachedDef,
                appId,
                [...fallbackPages],
                pageKey,
                action,
              );
            },
          });
          return;
        }
      }

      const webPages =
        cachedDef?.value?.webPages ||
        cachedDef?.webPages ||
        cachedDef?.value?.pages ||
        cachedDef?.pages;
      if (Array.isArray(webPages) && webPages.length > 0) {
        const sortedPages = [...webPages].sort((a: any, b: any) => {
          const orderA = a.priorityIndex ?? a.order ?? 1000;
          const orderB = b.priorityIndex ?? b.order ?? 1000;
          return orderA - orderB;
        });
        this.loadDirectDefinitionPage(
          cachedDef,
          appId,
          sortedPages,
          pageKey,
          action,
        );
        if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
          setTimeout(() => this.recalculateBubblePositions(), 200);
        }
        return;
      }
    }

    const handleAppDef = (appDef: any) => {
      if (!appDef) {
        this.error.set(this.configTexts.loadFailed);
        this.isLoading.set(false);
        return;
      }
      this.currentAppDef.set(appDef);
      if (!this.canAccessPreviewHarness(appDef)) {
        this.isLoading.set(false);
        this.notificationService.createNotification(
          'error',
          'Permission Denied',
          'Only the application owner or authorized administrators can access the App Builder Preview Harness.',
        );
        const fallbackRoute = appId || appDef.value?.route || appDef.key;
        this.router.navigate(['/app', fallbackRoute]);
        return;
      }
      this.loadedHarnessAppId = appId;
      this.initializeHarnessAppTheme(appId, projectId, appDef);
      // Lazy-load tasks ONLY when the Tasks panel is open
      if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
        this.ensureAppTasksLoaded();
      }
      this.appName.set(appDef.value?.name || appDef.name || 'App Preview');
      const resolvedAssetBaseUrl = this.resolveAssetBaseUrl(appDef, appId);
      this.activeAssetBaseUrl.set(resolvedAssetBaseUrl);

      const webPages =
        appDef.value?.webPages ||
        appDef.webPages ||
        appDef.value?.pages ||
        appDef.pages;

      if (Array.isArray(webPages) && webPages.length > 0) {
        const sortedPages = [...webPages].sort((a: any, b: any) => {
          const orderA = a.priorityIndex ?? a.order ?? 1000;
          const orderB = b.priorityIndex ?? b.order ?? 1000;
          return orderA - orderB;
        });

        this.installedManifestPages.set(sortedPages);

        if (appDef.value?.developBuild) {
          this.hasDevelopBuild.set(true);
          this.developBuildInfo.set(appDef.value.developBuild);
        }

        // If a compiled develop build exists on S3, prioritize loading it via fast CDN!
        if (appDef.value?.developBuild?.manifestUrl) {
          const rawUrl = String(
            appDef.value.developBuild.rawManifestUrl ||
              appDef.value.developBuild.manifestUrl,
          ).split('?')[0];
          const devManifestUrl = `${rawUrl}?b=${appDef.value.developBuild.buildNumber || 'dev'}`;
          if (isTrustedAppResourceUrl(devManifestUrl)) {
            this.loadedHarnessManifestUrl = devManifestUrl;
            this.httpClient.get<any>(devManifestUrl).subscribe({
              next: (manifest) => {
                this.processManifest(
                  manifest,
                  appId,
                  pageKey,
                  action,
                  devManifestUrl,
                );
              },
              error: (err) => {
                console.warn(
                  '[AppHarnessViewer] Develop manifest load failed, falling back to definition preview:',
                  err,
                );
                this.loadedHarnessManifestUrl = null;
                this.loadDirectDefinitionPage(
                  appDef,
                  appId,
                  sortedPages,
                  pageKey,
                  action,
                );
              },
            });
            return;
          }
          console.warn(
            '[AppHarnessViewer] Blocked untrusted develop manifest URL:',
            devManifestUrl,
          );
        }

        this.loadDirectDefinitionPage(
          appDef,
          appId,
          sortedPages,
          pageKey,
          action,
        );
        return;
      }

      this.error.set(this.configTexts.noPages);
      this.isLoading.set(false);
    };

    const navState = typeof window !== 'undefined' ? window.history?.state : null;
    if (navState?.preloadedAppDef && navState.preloadedAppDef.id === appId) {
      handleAppDef(navState.preloadedAppDef);
      if (navState.triggerBackgroundSync) {
        this.syncDevelopBuildInBackground(appId);
      }
      return;
    }

    this.definitionController.findById({ id: appId }).subscribe({
      next: (def) => handleAppDef(def),
      error: () => {
        this.error.set(this.configTexts.loadFailed);
        this.isLoading.set(false);
      },
    });
  }

  private syncDevelopBuildInBackground(appId: string): void {
    this.httpClient
      .post<any>(
        `${environment.apiBaseUrl}/definitions/${appId}/compile-app-develop`,
        { force: false, omitDefinitionInResponse: true },
      )
      .subscribe({
        next: (res) => {
          if (!res || res.skipped || this.currentAppId() !== appId) {
            return;
          }
          const developBuild = res?.developBuild || res;
          const buildNumber = developBuild?.buildNumber || res?.buildNumber;
          this.hasDevelopBuild.set(true);
          this.developBuildInfo.set(developBuild);

          HtmlUrlPreviewComponent.clearCache();

          const rawUrl =
            developBuild?.rawManifestUrl ||
            developBuild?.manifestUrl ||
            res?.rawManifestUrl ||
            res?.manifestUrl;
          if (rawUrl && isTrustedAppResourceUrl(rawUrl)) {
            const cleanRawUrl = String(rawUrl).split('?')[0];
            const devManifestUrl = `${cleanRawUrl}?b=${buildNumber || Date.now()}&t=${Date.now()}`;
            this.loadedHarnessManifestUrl = devManifestUrl;
            const pageKey = this.route.snapshot.paramMap.get('pageKey');
            const action = this.route.snapshot.paramMap.get('action');

            this.httpClient.get<any>(devManifestUrl).subscribe({
              next: (manifest) => {
                this.processManifest(
                  manifest,
                  appId,
                  pageKey,
                  action,
                  devManifestUrl,
                );
                this.notificationService.createNotification(
                  'info',
                  'Preview Updated',
                  'Synced latest changes from GitHub.',
                );
              },
            });
          }
        },
        error: (err) => {
          console.warn('[AppHarnessViewer] Background develop sync warning:', err);
        },
      });
  }

  private processManifest(
    manifest: any,
    appId: string,
    pageKey: string | null,
    action: string | null = null,
    manifestUrl: string | null = null,
  ): void {
    this.activeManifest.set(manifest);
    if (!manifest || !manifest.pages || manifest.pages.length === 0) {
      this.error.set(this.configTexts.noPages);
      this.isLoading.set(false);
      return;
    }

    let baseCdnUrl = '';
    if (manifestUrl) {
      const lastSlashIndex = manifestUrl.lastIndexOf('/');
      baseCdnUrl =
        lastSlashIndex !== -1
          ? manifestUrl.substring(0, lastSlashIndex)
          : manifestUrl;
    }

    const resolvedAssetBaseUrl = this.resolveAssetBaseUrl(
      manifest,
      appId,
      manifestUrl,
    );
    this.activeAssetBaseUrl.set(
      resolvedAssetBaseUrl && isTrustedAppResourceUrl(resolvedAssetBaseUrl)
        ? resolvedAssetBaseUrl
        : '',
    );

    const mappedPages = manifest.pages.map((p: any) => {
      const rawHtmlUrl =
        p.publicUrl ||
        p.htmlUrl ||
        (p.htmlFilePath && baseCdnUrl
          ? `${baseCdnUrl}/${p.htmlFilePath}`
          : undefined);
      return {
        ...p,
        htmlUrl:
          rawHtmlUrl && isTrustedAppResourceUrl(rawHtmlUrl)
            ? rawHtmlUrl
            : undefined,
      };
    });

    this.installedManifestPages.set(mappedPages);

    const sortedPages = mappedPages.sort((a: any, b: any) => {
      const orderA = a.priorityIndex ?? a.order ?? 1000;
      const orderB = b.priorityIndex ?? b.order ?? 1000;
      return orderA - orderB;
    });

    if (!pageKey) {
      const firstPage = sortedPages[0];
      const targetRoute = firstPage.route || firstPage.id || firstPage.key;
      this.router.navigate(['/app-builder', appId, 'preview', targetRoute], {
        replaceUrl: true,
      });
      return;
    }

    const cleanKey = pageKey.trim();
    const decodedKey = decodeURIComponent(cleanKey);

    let searchKey = cleanKey;
    if (action) {
      searchKey = `${cleanKey}/${action}`;
    }
    const decodedSearchKey = decodeURIComponent(searchKey);
    const searchKeyDash = action ? `${cleanKey}-${action}` : cleanKey;
    const decodedSearchKeyDash = decodeURIComponent(searchKeyDash);

    let targetPageDef = sortedPages.find(
      (p: any) =>
        p.route === searchKey ||
        p.route === decodedSearchKey ||
        p.route === searchKeyDash ||
        p.route === decodedSearchKeyDash ||
        p.id === searchKey ||
        p.key === searchKey ||
        p.name?.toLowerCase() === searchKey.toLowerCase() ||
        p.name?.toLowerCase() === searchKeyDash.toLowerCase(),
    );

    if (!targetPageDef) {
      targetPageDef = sortedPages.find(
        (p: any) => p.route === `${cleanKey}/:id`,
      );
    }

    if (!targetPageDef) {
      targetPageDef = sortedPages.find(
        (p: any) =>
          p.route === cleanKey ||
          p.route === decodedKey ||
          p.id === cleanKey ||
          p.key === cleanKey ||
          p.name?.toLowerCase() === cleanKey.toLowerCase(),
      );
    }

    targetPageDef = targetPageDef || sortedPages[0];
    const pageIdent = targetPageDef.route || targetPageDef.key || targetPageDef.id || cleanKey;

    const candidatePageUrl = targetPageDef.publicUrl || targetPageDef.htmlUrl;
    if (candidatePageUrl && !isTrustedAppResourceUrl(candidatePageUrl)) {
      console.error(
        '[AppHarnessViewer] Blocked untrusted page URL:',
        candidatePageUrl,
      );
      this.error.set('Blocked untrusted application page URL.');
      this.isLoading.set(false);
      return;
    }

    this.activePageId.set(pageIdent);
    this.activePageRoute.set(pageIdent);
    this.activePageType.set(targetPageDef.type || 'AppPage');
    this.activePageUrl.set(candidatePageUrl || null);
    this.bubblePositions.set({});
    this.isLoading.set(false);
  }

  private loadDirectDefinitionPage(
    appDef: any,
    appId: string,
    sortedPages: any[],
    pageKey: string | null,
    action: string | null = null,
  ): void {
    if (!sortedPages || sortedPages.length === 0) {
      this.error.set(this.configTexts.noPages);
      this.isLoading.set(false);
      return;
    }

    this.installedManifestPages.set(sortedPages);

    if (!pageKey) {
      const firstPage = sortedPages[0];
      const targetRoute = firstPage.route || firstPage.id || firstPage.key;
      this.router.navigate(['/app-builder', appId, 'preview', targetRoute], {
        replaceUrl: true,
      });
      return;
    }

    const cleanKey = pageKey.trim();
    const decodedKey = decodeURIComponent(cleanKey);

    let searchKey = cleanKey;
    if (action) {
      searchKey = `${cleanKey}/${action}`;
    }
    const decodedSearchKey = decodeURIComponent(searchKey);
    const searchKeyDash = action ? `${cleanKey}-${action}` : cleanKey;
    const decodedSearchKeyDash = decodeURIComponent(searchKeyDash);

    let targetPageDef = sortedPages.find(
      (p: any) =>
        p.route === searchKey ||
        p.route === decodedSearchKey ||
        p.route === searchKeyDash ||
        p.route === decodedSearchKeyDash ||
        p.id === searchKey ||
        p.key === searchKey ||
        p.name?.toLowerCase() === searchKey.toLowerCase() ||
        p.name?.toLowerCase() === searchKeyDash.toLowerCase(),
    );

    if (!targetPageDef) {
      targetPageDef = sortedPages.find(
        (p: any) => p.route === `${cleanKey}/:id`,
      );
    }

    if (!targetPageDef) {
      targetPageDef = sortedPages.find(
        (p: any) =>
          p.route === cleanKey ||
          p.route === decodedKey ||
          p.id === cleanKey ||
          p.key === cleanKey ||
          p.name?.toLowerCase() === cleanKey.toLowerCase(),
      );
    }

    targetPageDef = targetPageDef || sortedPages[0];
    const pageIdent = targetPageDef.route || targetPageDef.key || targetPageDef.id || cleanKey;

    this.activePageRoute.set(pageIdent);
    this.activePageId.set(appDef.id || appDef._id);
    this.activePageType.set('AppPage');
    this.activePageUrl.set(null);
    this.activePageFileName.set(
      targetPageDef.htmlFilePath ||
        targetPageDef.route ||
        `${cleanKey}.html`,
    );
    this.bubblePositions.set({});
    this.isLoading.set(false);
  }

  private resolveAssetBaseUrl(
    item: any,
    appId: string,
    manifestUrl: string | null = null,
  ): string {
    if (item?.assetBaseUrl) return item.assetBaseUrl;
    if (item?.value?.assetBaseUrl) return item.value.assetBaseUrl;
    if (item?.value?.developBuild?.assetBaseUrl) {
      return item.value.developBuild.assetBaseUrl;
    }
    const candidateManifestUrl =
      manifestUrl ||
      item?.value?.developBuild?.rawManifestUrl ||
      item?.value?.developBuild?.manifestUrl;
    if (candidateManifestUrl) {
      const cleanUrl = String(candidateManifestUrl).split('?')[0];
      const lastSlashIndex = cleanUrl.lastIndexOf('/');
      if (lastSlashIndex !== -1) {
        const baseDir = cleanUrl.substring(0, lastSlashIndex);
        return baseDir.endsWith('/assets') ? baseDir : `${baseDir}/assets`;
      }
    }
    if (appId) {
      return `https://rapider.nyc3.cdn.digitaloceanspaces.com/app/${appId}/develop/assets`;
    }
    return '';
  }

  public setViewportMode(mode: 'desktop' | 'tablet' | 'mobile'): void {
    this.viewportMode.set(mode);
    setTimeout(() => this.recalculateBubblePositions(), 100);
    setTimeout(() => {
      this.recalculateBubblePositions();
      const iframe = this.captureZoneRef?.nativeElement?.querySelector(
        'iframe',
      ) as HTMLIFrameElement;
      if (iframe?.contentWindow) {
        try {
          iframe.contentWindow.dispatchEvent(new Event('resize'));
        } catch {}
      }
    }, 350);
  }

  public toggleDevTools(): void {
    this.isDevToolsOpen.update((v) => !v);
    setTimeout(() => this.recalculateBubblePositions(), 350);
  }

  public clearApiLogs(): void {
    this.apiLogs.set([]);
    this.selectedLogItem.set(null);
  }

  public setApiLogFilter(
    filter: 'all' | 'request' | 'response' | 'error' | 'broadcast',
  ): void {
    this.apiLogFilter.set(filter);
  }

  public selectLogItem(item: any): void {
    this.selectedLogItem.set(item);
  }

  public getLogSummary(log: any): string {
    if (!log) return '';
    if (log.payload) {
      try {
        const str =
          typeof log.payload === 'string'
            ? log.payload
            : JSON.stringify(log.payload);
        return str.length > 80 ? str.substring(0, 80) + '...' : str;
      } catch {
        return '';
      }
    }
    if (log.response) {
      try {
        const str =
          typeof log.response === 'string'
            ? log.response
            : JSON.stringify(log.response);
        return str.length > 80 ? str.substring(0, 80) + '...' : str;
      } catch {
        return '';
      }
    }
    if (log.error) {
      return typeof log.error === 'string'
        ? log.error
        : JSON.stringify(log.error);
    }
    return '';
  }

  public formatLogJson(item: any): string {
    if (!item) return '';
    try {
      return JSON.stringify(item, null, 2);
    } catch {
      return String(item);
    }
  }

  public copyLogPayload(log: any): void {
    if (!log) return;
    const dataToCopy = log.payload || log.response || log;
    try {
      navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2));
      this.notificationService.createNotification(
        'success',
        'Copied',
        'Log data copied to clipboard.',
      );
    } catch {
      // ignore
    }
  }

  public handleApiEvent(event: any): void {
    if (!event) return;
    this.apiLogs.update((logs) => [
      {
        id: uuidv4(),
        timestamp: event.timestamp || new Date(),
        type: event.type || 'request',
        operation: event.operation || '',
        entityName: event.entityName || '',
        payload: event.payload,
        response: event.response,
        error: event.error,
        eventName: event.eventName,
        reqId: event.reqId,
      },
      ...logs.slice(0, 199),
    ]);
  }

  public async compileDevelopBuild(): Promise<void> {
    const appId = this.currentAppId();
    if (!appId) return;

    this.isRecompiling.set(true);
    this.recompileStatusText.set('Compiling Dev...');

    try {
      // Compile develop version from GitHub (which also syncs app.manifest.json & webPages)
      const res = await firstValueFrom(
        this.httpClient.post<any>(
          `${environment.apiBaseUrl}/definitions/${appId}/compile-app-develop`,
          { force: true },
        ),
      );

      if (res?.appDefinition) {
        this.currentAppDef.set(res.appDefinition);
        if (res.appDefinition.value?.name || res.appDefinition.name) {
          this.appName.set(res.appDefinition.value?.name || res.appDefinition.name);
        }
      }

      const developBuild = res?.developBuild || res;
      const buildNumber = developBuild?.buildNumber || res?.buildNumber;

      this.hasDevelopBuild.set(true);
      this.developBuildInfo.set(developBuild);

      if (res?.skipped) {
        this.notificationService.createNotification(
          'info',
          'Dev Preview Up to Date',
          `Develop version is already deployed with commit SHA ${res.commitSha?.substring(0, 7) || 'latest'}. Reloading preview...`,
        );
      } else {
        this.notificationService.createNotification(
          'success',
          'Dev Preview Recompiled',
          `Synced from GitHub and Build #${buildNumber || ''} created successfully. Reloading preview...`,
        );
      }

      HtmlUrlPreviewComponent.clearCache();

      const rawUrl = developBuild?.rawManifestUrl || developBuild?.manifestUrl || res?.rawManifestUrl || res?.manifestUrl;
      if (rawUrl && isTrustedAppResourceUrl(rawUrl)) {
        const cleanRawUrl = String(rawUrl).split('?')[0];
        const devManifestUrl = `${cleanRawUrl}?b=${buildNumber || Date.now()}&t=${Date.now()}`;
        this.loadedHarnessManifestUrl = devManifestUrl;
        const pageKey = this.route.snapshot.paramMap.get('pageKey');
        const action = this.route.snapshot.paramMap.get('action');

        this.httpClient.get<any>(devManifestUrl).subscribe({
          next: (manifest) => {
            this.processManifest(
              manifest,
              appId,
              pageKey,
              action,
              devManifestUrl,
            );
          },
        });
      }
    } catch (err: any) {
      const msg =
        err?.error?.error?.message ||
        err?.error?.message ||
        'Failed to compile develop version from GitHub.';
      this.notificationService.createNotification(
        'error',
        'Recompile Failed',
        msg,
      );
    } finally {
      this.isRecompiling.set(false);
      this.recompileStatusText.set('Recompile Dev');
    }
  }

  public navigateToPage(pageRoute: string): void {
    const appId = this.currentAppId();
    if (appId && pageRoute) {
      this.isLoading.set(true);
      this.router.navigate(['/app-builder', appId, 'preview', pageRoute]);
    }
  }

  private cleanRoute(route: string | null | undefined): string {
    return (route || '').trim().toLowerCase().replace(/^\/+|\/+$/g, '');
  }

  private async waitForReadyIframe(
    expectedRoute?: string,
    timeoutMs = 15000,
  ): Promise<HTMLIFrameElement> {
    const startTime = Date.now();
    const cleanExpected = expectedRoute ? this.cleanRoute(expectedRoute) : null;

    // Small delay to allow Angular router navigation to take effect
    await new Promise((r) => setTimeout(r, 250));

    while (Date.now() - startTime < timeoutMs) {
      const currentRoute = this.cleanRoute(this.activePageRoute());
      const currentId = this.cleanRoute(this.activePageId());
      const routeMatches =
        !cleanExpected ||
        currentRoute === cleanExpected ||
        currentId === cleanExpected;

      if (!this.isLoading() && routeMatches) {
        const iframe = (this.captureZoneRef?.nativeElement?.querySelector('iframe') ||
          document.querySelector('iframe')) as HTMLIFrameElement;

        if (iframe && iframe.contentWindow && iframe.contentDocument) {
          const doc = iframe.contentDocument;
          const isDocReady =
            doc.readyState === 'complete' || doc.readyState === 'interactive';
          const hasBodyContent =
            doc.body &&
            (doc.body.children.length > 0 ||
              (doc.body.innerHTML && doc.body.innerHTML.trim().length > 50));

          if (isDocReady && hasBodyContent) {
            // Settle time for scripts & rapiderApi in iframe
            await new Promise((r) => setTimeout(r, 350));
            return iframe;
          }
        }
      }
      await new Promise((r) => setTimeout(r, 150));
    }

    throw new Error(
      `App iframe was not ready or accessible after navigation to '${expectedRoute || 'target'}' (${timeoutMs}ms).`,
    );
  }

  public goBack(): void {
    const appId = this.currentAppId();
    if (appId) {
      this.router.navigate(['/app-builder', appId]);
      return;
    }
    this.router.navigate(['/app-builder']);
  }

  public getActivePageName(): string {
    const activeRoute = this.activePageRoute() || this.activePageId();
    const pages = this.installedManifestPages();
    const clean = activeRoute ? activeRoute.trim().toLowerCase().replace(/^\/+|\/+$/g, '') : '';
    const page = pages.find((p) => {
      const pRoute = (p.route || '').toLowerCase().replace(/^\/+|\/+$/g, '');
      const pKey = (p.key || '').toLowerCase();
      const pId = (p.id || '').toLowerCase();
      const pName = (p.name || p.title || '').toLowerCase();
      return (
        pRoute === clean ||
        pKey === clean ||
        pId === clean ||
        pName === clean ||
        pRoute === `${clean}/:id` ||
        `${pRoute}/:id` === clean
      );
    });
    return page?.name || page?.title || activeRoute || 'Overview';
  }

  // Sidebar controls
  public openTasksSidebar(): void {
    if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
      this.isSidebarOpen.set(false);
      return;
    }
    this.ensureTasksSidebarOpen();
  }

  public ensureTasksSidebarOpen(): void {
    const wasClosedOrOtherTab =
      !this.isSidebarOpen() || this.activeSidebarTab() !== 'Tasks';
    this.activeSidebarTab.set('Tasks');
    this.isSidebarOpen.set(true);
    this.ensureAppTasksLoaded();
    if (wasClosedOrOtherTab) {
      setTimeout(() => this.recalculateBubblePositions(), 200);
    }
  }

  public openE2ESidebar(): void {
    if (this.isSidebarOpen() && this.activeSidebarTab() === 'E2E Tests') {
      this.isSidebarOpen.set(false);
      return;
    }
    this.activeSidebarTab.set('E2E Tests');
    this.isSidebarOpen.set(true);
  }

  public onCaptureAllChange(val: boolean): void {
    this.captureScreenshots.set(val);
    if (val) {
      this.captureScreenshotsOnFailure.set(false);
    }
  }

  public onCaptureFailureChange(val: boolean): void {
    this.captureScreenshotsOnFailure.set(val);
    if (val) {
      this.captureScreenshots.set(false);
    }
  }

  public previewScreenshot(url: string): void {
    this.selectedScreenshot.set(url);
    this.isScreenshotModalVisible.set(true);
  }

  public closeScreenshotModal(): void {
    this.isScreenshotModalVisible.set(false);
    this.selectedScreenshot.set(null);
  }

  public openEditApprovalModal(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    if (task.status !== 'Open' && task.status !== 'open' && task.status !== 'Accepted' && task.status !== 'Approved' && task.status !== 'Closed') {
      this.notificationService.createNotification(
        'warning',
        'Cannot Edit Approval',
        'Only tasks in initial open/approval status can have their approval status modified.'
      );
      return;
    }
    const val: 'approved' | 'rejected' = task.isApproved === false ? 'rejected' : 'approved';
    this.editingApprovalTask.set(task);
    this.editingApprovalValue.set(val);
    this.editingApprovalInitialValue.set(val);
    this.isEditApprovalModalVisible.set(true);
  }

  public closeEditApprovalModal(): void {
    this.isEditApprovalModalVisible.set(false);
    this.editingApprovalTask.set(null);
  }

  public async confirmEditApproval(): Promise<void> {
    const task = this.editingApprovalTask();
    if (!task) {
      this.closeEditApprovalModal();
      return;
    }

    const currentVal = this.editingApprovalValue();
    const initialVal = this.editingApprovalInitialValue();

    if (currentVal === initialVal) {
      this.closeEditApprovalModal();
      return;
    }

    const newApproved = currentVal === 'approved';
    await this.setTaskApproval(task, newApproved);
    this.closeEditApprovalModal();
  }

  public stopE2ETests(): void {
    this.e2eRunner.stop();
    this.testLogs.update((logs) => [
      ...logs,
      {
        type: 'system',
        message: 'Test execution stopped by user.',
        success: false,
        timestamp: Date.now(),
      },
    ]);
  }

  public async runE2ETests(): Promise<void> {
    if (this.isTestRunning()) return;
    this.isTestRunning.set(true);
    this.e2eRunner.isStopped = false;

    this.isSettingsCollapsed.set(true);
    this.testLogs.set([
      {
        type: 'system',
        message: 'Initializing E2E Test Runner Engine...',
        success: true,
        timestamp: Date.now(),
      },
      {
        type: 'system',
        message: 'Loading expectations schema from Artifacts...',
        success: true,
        timestamp: Date.now(),
      },
    ]);

    let captureVideo: HTMLVideoElement | null = null;
    let captureCanvas: HTMLCanvasElement | null = null;
    let captureStream: MediaStream | null = null;
    let mediaRecorder: MediaRecorder | null = null;
    const recordedChunks: Blob[] = [];

    this.testMode.set(true);

    if (this.captureScreenshots() || this.captureScreenshotsOnFailure() || this.recordVideo()) {
      try {
        captureStream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'browser' },
          audio: false,
        });
        captureVideo = document.createElement('video');
        captureVideo.srcObject = captureStream;
        await captureVideo.play();
        captureCanvas = document.createElement('canvas');

        if (this.recordVideo() && typeof MediaRecorder !== 'undefined') {
          let mimeType = 'video/webm;codecs=vp9,opus';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8,opus';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
          mediaRecorder = mimeType
            ? new MediaRecorder(captureStream, { mimeType })
            : new MediaRecorder(captureStream);
          mediaRecorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              recordedChunks.push(event.data);
            }
          };
          mediaRecorder.start(1000);
        }
      } catch (e) {
        this.testLogs.update((logs) => [
          ...logs,
          {
            type: 'system',
            message:
              'Warning: Screen capture permission denied. Screenshots and video recording will be disabled.',
            success: false,
            timestamp: Date.now(),
          },
        ]);
      }
    }

    const sub = this.e2eRunner.results$.subscribe((res) => {
      let screenshotBase64: string | null = null;
      let shouldCapture = false;
      if (res.success !== null) {
        if (this.captureScreenshots()) shouldCapture = true;
        if (this.captureScreenshotsOnFailure() && res.success === false) shouldCapture = true;
      }

      if (shouldCapture && captureVideo && captureCanvas && captureVideo.videoWidth > 0) {
        captureCanvas.width = captureVideo.videoWidth;
        captureCanvas.height = captureVideo.videoHeight;
        const ctx = captureCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(
            captureVideo,
            0,
            0,
            captureCanvas.width,
            captureCanvas.height,
          );
          screenshotBase64 = captureCanvas.toDataURL('image/jpeg', 0.5);
        }
      }

      this.testLogs.update((logs) => {
        const idx = logs.findIndex(
          (l) => l.suiteName === res.suiteName && l.stepNumber === res.stepNumber && l.type === 'test'
        );
        if (idx > -1) {
          const updated = [...logs];
          updated[idx] = {
            ...updated[idx],
            success: res.success,
            error: res.message || updated[idx].error,
            timestamp: Date.now(),
            screenshot: screenshotBase64 || updated[idx].screenshot,
          };
          return updated;
        }
        return [
          ...logs,
          {
            type: 'test',
            suiteName: res.suiteName,
            stepName: res.stepName,
            stepNumber: res.stepNumber,
            explanation: res.explanation,
            action: res.action,
            success: res.success,
            error: res.message,
            timestamp: Date.now(),
            screenshot: screenshotBase64,
          },
        ];
      });

      setTimeout(() => {
        const activeCard = document.querySelector('#active-test-card');
        const container = document.querySelector('.e2e-timeline-container');
        if (activeCard) {
          activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 50);
    });

    const iframe = (this.captureZoneRef?.nativeElement?.querySelector('iframe') ||
      document.querySelector('iframe')) as HTMLIFrameElement;
    if (!iframe) {
      this.testLogs.update((logs) => [
        ...logs,
        {
          type: 'system',
          message: 'Error: Cannot find iframe to execute tests against.',
          success: false,
          timestamp: Date.now(),
        },
      ]);
      if (captureStream)
        captureStream.getTracks().forEach((track) => track.stop());
      this.isTestRunning.set(false);
      return;
    }

    const manifest = this.activeManifest();
    const e2eFile =
      manifest?.e2eTests?.expectationsFile || 'e2e/expectations.json';
    let testUrl = manifest?.e2eTests?.expectationsFileUrl;

    if (!testUrl) {
      let baseUrl = this.activeAssetBaseUrl();
      if (baseUrl) {
        baseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
      } else {
        const appId = this.currentAppId() || '';
        baseUrl = appId ? `/assets/${appId}/` : '/assets/';
      }
      testUrl = baseUrl + e2eFile;
    }

    const runTestsWithConfig = async (config: any) => {
      try {
        this.testLogs.update((logs) => [
          ...logs,
          {
            type: 'system',
            message: `Found test config for ${config.appName}`,
            success: true,
            timestamp: Date.now(),
          },
        ]);
        let suitesToRun = config.testSuites || [];
        if (this.testActivePageOnly()) {
          const currentRoute = this.cleanRoute(this.activePageRoute());
          const currentId = this.cleanRoute(this.activePageId());
          const matched = suitesToRun.filter((s: any) => {
            const sRoute = this.cleanRoute(s.route || s.suiteName);
            return (
              sRoute === currentRoute ||
              sRoute === currentId ||
              s.suiteName?.toLowerCase().includes(currentRoute)
            );
          });
          if (matched.length > 0) {
            suitesToRun = matched;
          }
        }

        for (const suite of suitesToRun) {
          if (this.e2eRunner.isStopped) {
            break;
          }
          this.testLogs.update((logs) => [
            ...logs,
            {
              type: 'suite',
              suiteName: suite.suiteName,
              message: `Running Page: ${suite.suiteName}...`,
              success: true,
              timestamp: Date.now(),
            },
          ]);

          const targetRoute = suite.route || suite.suiteName;
          const cleanTarget = this.cleanRoute(targetRoute);
          const currentRoute = this.cleanRoute(this.activePageRoute());
          const currentId = this.cleanRoute(this.activePageId());

          if (cleanTarget && currentRoute !== cleanTarget && currentId !== cleanTarget) {
            this.navigateToPage(suite.route || targetRoute);
          }

          let currentIframe: HTMLIFrameElement;
          try {
            currentIframe = await this.waitForReadyIframe(targetRoute, 15000);
          } catch (iframeErr: any) {
            this.testLogs.update((logs) => [
              ...logs,
              {
                type: 'system',
                message: `FAILED: App iframe lost after navigation to ${suite.suiteName}: ${iframeErr.message}`,
                success: false,
                timestamp: Date.now(),
              },
            ]);
            continue;
          }

          const res = await this.e2eRunner.runTestSuite(
            suite.suiteName,
            suite.tests,
            currentIframe,
            {
              virtualMouse: this.virtualMouse(),
              suiteRoute: suite.route || targetRoute,
              onNavigate: async (stepRoute: string) => {
                const cleanStepRoute = this.cleanRoute(stepRoute);
                const curRoute = this.cleanRoute(this.activePageRoute());
                const curId = this.cleanRoute(this.activePageId());
                if (
                  cleanStepRoute &&
                  curRoute !== cleanStepRoute &&
                  curId !== cleanStepRoute
                ) {
                  this.navigateToPage(stepRoute);
                  currentIframe = await this.waitForReadyIframe(stepRoute, 12000);
                  return currentIframe;
                }
                const liveIframe = (this.captureZoneRef?.nativeElement?.querySelector('iframe') ||
                  document.querySelector('iframe')) as HTMLIFrameElement;
                if (liveIframe) {
                  currentIframe = liveIframe;
                }
                return currentIframe;
              },
            },
          );
          this.testLogs.update((logs) => [
            ...logs,
            {
              type: 'system',
              message: `Page test Finished: ${res.passed} passed, ${res.failed} failed.`,
              success: res.failed === 0,
              timestamp: Date.now(),
            },
          ]);
          if (this.e2eRunner.isStopped) break;
        }
      } catch (e: any) {
        this.testLogs.update((logs) => [
          ...logs,
          {
            type: 'system',
            message: 'Test Engine Exception: ' + e.message,
            success: false,
            timestamp: Date.now(),
          },
        ]);
      } finally {
        sub.unsubscribe();
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          await new Promise<void>((resolve) => {
            mediaRecorder!.onstop = () => resolve();
            try {
              mediaRecorder!.stop();
            } catch {
              resolve();
            }
          });

          if (recordedChunks.length > 0) {
            const finalMime = mediaRecorder.mimeType || 'video/webm';
            const blob = new Blob(recordedChunks, { type: finalMime });
            const videoUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = videoUrl;
            const cleanAppName = (this.appName() || 'app')
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-');
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `${cleanAppName}-e2e-recording-${timestamp}.webm`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(videoUrl);
            }, 1500);

            this.testLogs.update((logs) => [
              ...logs,
              {
                type: 'system',
                message: `Video recording saved: ${filename} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`,
                success: true,
                timestamp: Date.now(),
              },
            ]);
          }
        }
        if (captureStream)
          captureStream.getTracks().forEach((track) => track.stop());
        this.testMode.set(false);
        this.isTestRunning.set(false);
      }
    };

    this.httpClient.get<any>(testUrl).subscribe({
      next: (config) => runTestsWithConfig(config),
      error: () => {
        const altUrl = testUrl.includes('/assets/')
          ? testUrl.replace('/assets/', '/')
          : testUrl.replace(/\/e2e\//, '/assets/e2e/');

        const tryLocalFallback = () => {
          const appId = this.currentAppId() || '';
          const localFallbackUrl = appId ? `/assets/${appId}/${e2eFile}` : `/assets/${e2eFile}`;
          this.testLogs.update((logs) => [
            ...logs,
            {
              type: 'system',
              message: `Primary fetch failed. Falling back to local app assets: ${localFallbackUrl}`,
              success: true,
              timestamp: Date.now(),
            },
          ]);
          this.httpClient.get<any>(localFallbackUrl).subscribe({
            next: (config) => runTestsWithConfig(config),
            error: () => {
              this.testLogs.update((logs) => [
                ...logs,
                {
                  type: 'system',
                  message: `Failed to load ${e2eFile} from app assets.`,
                  success: false,
                  timestamp: Date.now(),
                },
              ]);
              sub.unsubscribe();
              if (captureStream)
                captureStream.getTracks().forEach((track) => track.stop());
              this.isTestRunning.set(false);
            },
          });
        };

        if (altUrl !== testUrl) {
          this.httpClient.get<any>(altUrl).subscribe({
            next: (config) => runTestsWithConfig(config),
            error: () => tryLocalFallback(),
          });
        } else {
          tryLocalFallback();
        }
      },
    });
  }

  public getTestReportMarkdown(): string {
    const logs = this.testLogs();
    if (logs.length === 0) return '';

    let mdContent = `# E2E Test Report\n\n`;
    mdContent += `**Date:** ${new Date().toLocaleString()}\n`;
    mdContent += `**App ID:** ${this.currentAppId()}\n`;
    mdContent += `**Page:** ${this.activePageRoute() || this.activePageId()}\n\n`;
    mdContent += `## Execution Logs\n\n`;

    for (const log of logs) {
      const time = new Date(log.timestamp).toISOString();
      if (log.type === 'system') {
        mdContent += `- [${time}] **SYSTEM**: ${log.message} ${log.success ? '✅' : '❌'}\n`;
      } else if (log.type === 'suite') {
        mdContent += `\n### Page: ${log.suiteName}\n`;
      } else {
        const icon = log.success === null ? '⏳' : log.success ? '✅' : '❌';
        mdContent += `1. **${log.stepName}** ${icon}\n`;
        if (log.explanation) mdContent += `   - *Explanation:* ${log.explanation}\n`;
        if (log.action) mdContent += `   - *Action:* \`${log.action}\`\n`;
        if (!log.success && log.error) mdContent += `   - *Error:* ${log.error}\n`;
      }
    }
    return mdContent;
  }

  public previewCurrentTestReport(): void {
    const mdContent = this.getTestReportMarkdown();
    if (!mdContent) return;
    this.selectedReportContent.set(mdContent);
    this.isReportPreviewVisible.set(true);
  }

  public downloadTestReport(): void {
    const mdContent = this.getTestReportMarkdown();
    if (!mdContent) return;

    const blob = new Blob([mdContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `e2e-report-${this.currentAppId() || 'app'}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  public toggleAuditPageExpanded(pagePath: string): void {
    if (this.expandedAuditPage() === pagePath) {
      this.expandedAuditPage.set(null);
    } else {
      this.expandedAuditPage.set(pagePath);
    }
  }

  public copyAiRemediationPrompt(): void {
    const data = this.qaAuditData();
    if (!data) return;

    let prompt = `# Rapider UI Page Fix Instructions\n\n`;
    prompt += `**App:** ${data.app}\n`;
    prompt += `**Compliance Rate:** ${data.complianceRate}%\n\n`;
    prompt += `Please fix the following detected rule violations across the pages:\n\n`;

    let issueCount = 0;
    for (const page of data.pages || []) {
      if ((page.issues && page.issues.length > 0) || (page.warnings && page.warnings.length > 0)) {
        prompt += `### Page: \`${page.pagePath}\`\n`;
        for (const iss of page.issues || []) {
          issueCount++;
          prompt += `- ❌ **[${iss.rule}] ${iss.type}**: ${iss.message}\n`;
          if (iss.snippet) prompt += `  \`\`\`html\n  ${iss.snippet}\n  \`\`\`\n`;
        }
        for (const warn of page.warnings || []) {
          prompt += `- ⚠️ **[${warn.rule}] ${warn.type}**: ${warn.message}\n`;
          if (warn.snippet) prompt += `  \`\`\`html\n  ${warn.snippet}\n  \`\`\`\n`;
        }
        prompt += `\n`;
      }
    }

    if (issueCount === 0) {
      prompt += `All pages are currently passing all strict rules! Keep all primary buttons with #ffffff !important font color, zero native alerts, and all buttons wired.\n`;
    }

    navigator.clipboard.writeText(prompt);
    this.notificationService.createNotification(
      'success',
      'Prompt Copied',
      'AI remediation prompt copied to clipboard.',
    );
  }

  public toggleArtifactsSidebar(): void {
    this.activeSidebarTab.set('Artifacts');
    this.isSidebarOpen.update((v) => !v);
  }

  public closeSidebar(): void {
    this.isSidebarOpen.set(false);
  }

  public onSidebarSplitResize(sizes: (number | string)[]): void {
    if (sizes && sizes.length > 1) {
      const rightSize = sizes[1];
      if (typeof rightSize === 'number') {
        this.sidebarWidthPx.set(rightSize);
      }
    }
    this.recalculateBubblePositions();
  }

  public onSidebarSplitResizeEnd(sizes: (number | string)[]): void {
    this.onSidebarSplitResize(sizes);
    this.recalculateBubblePositions();
  }

  public handleCaptureZoneMouseMove(event: MouseEvent): void {
    if (!this.isAddCommentMode()) return;
    const doc = this.getLocalDoc();
    if (!doc) return;
    const target = event.target as HTMLElement;
    if (
      target === doc.body ||
      target === doc.documentElement ||
      (this.canvasWrapperRef && target === this.canvasWrapperRef.nativeElement) ||
      (this.captureZoneRef && target === this.captureZoneRef.nativeElement)
    ) {
      return;
    }

    doc
      .querySelectorAll('.comment-hover-target')
      .forEach((el) => el.classList.remove('comment-hover-target'));
    target.classList.add('comment-hover-target');
  }

  public handleCaptureZoneMouseOut(_event: MouseEvent): void {
    const doc = this.getLocalDoc();
    if (!doc) return;
    doc
      .querySelectorAll('.comment-hover-target')
      .forEach((el) => el.classList.remove('comment-hover-target'));
  }

  public onCaptureZoneWheel(_event: WheelEvent): void {
    this.triggerScrollHiding();
  }

  public onCaptureZoneScroll(_event: Event): void {
    this.triggerScrollHiding();
  }

  private generateUniqueSelector(el: HTMLElement): string {
    if (!el) return '';
    const path: string[] = [];
    let current: HTMLElement | null = el;
    while (current && current.nodeType === 1) {
      let selector = current.nodeName.toLowerCase();
      if (current.id) {
        selector += `#${current.id}`;
        path.unshift(selector);
        break;
      }

      let sibling = current.previousElementSibling;
      let nth = 1;
      while (sibling) {
        if (sibling.nodeName.toLowerCase() === selector) {
          nth++;
        }
        sibling = sibling.previousElementSibling;
      }
      selector += `:nth-of-type(${nth})`;
      path.unshift(selector);
      current = current.parentElement;
    }
    return path.join(' > ');
  }

  public handleCaptureZoneClick(event: MouseEvent): void {
    if (
      !this.isAddCommentMode() &&
      !(typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const target = event.target as HTMLElement;
    let elementId = target?.getAttribute ? target.getAttribute('data-rapider-node-id') : null;
    if (!elementId && target?.setAttribute) {
      elementId = uuidv4();
      target.setAttribute('data-rapider-node-id', elementId);
    }

    const rect = target?.getBoundingClientRect
      ? target.getBoundingClientRect()
      : { left: event.clientX, top: event.clientY };
    const offsetX = Math.round(event.clientX - rect.left);
    const offsetY = Math.round(event.clientY - rect.top);

    this.pinnedMetadata.set({
      elementId: elementId || uuidv4(),
      uniqueSelector: target ? this.generateUniqueSelector(target) : '',
      offsetX: offsetX,
      offsetY: offsetY,
      absoluteX: Math.round(event.clientX),
      absoluteY: Math.round(event.clientY),
      pageId: this.activePageRoute() || this.activePageId() || '',
      tag: target?.tagName ? target.tagName.toLowerCase() : 'div',
    });

    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(true);
    // Keep comment mode flag active briefly to swallow trailing mouseup/click events
    setTimeout(() => {
      this.cancelAddCommentMode();
    }, 150);
  }

  public setCommentMode(active: boolean): void {
    this.isAddCommentMode.set(active);
    if (typeof window !== 'undefined') {
      (window as any).__rapiderCommentModeActive = active;
    }
    const doc = this.getLocalDoc();
    if (doc) {
      if (active) {
        doc.documentElement.classList.add('rapider-comment-mode');
        if (doc.defaultView) {
          (doc.defaultView as any).__rapiderCommentModeActive = true;
        }
      } else {
        doc.documentElement.classList.remove('rapider-comment-mode');
        doc
          .querySelectorAll('.comment-hover-target')
          .forEach((el) => el.classList.remove('comment-hover-target'));
        if (doc.defaultView) {
          (doc.defaultView as any).__rapiderCommentModeActive = false;
        }
      }
    }
  }

  public cancelAddCommentMode(): void {
    this.setCommentMode(false);
    if (this.isPinningFromModal) {
      this.isPinningFromModal = false;
      this.isCreateTaskModalVisible.set(true);
    }
  }

  public toggleShowBubbles(): void {
    this.showBubbles.update((v) => !v);
    if (this.showBubbles()) {
      setTimeout(() => this.recalculateBubblePositions(), 100);
    }
  }

  public onBubbleClick(task: UnifiedTaskItem, event: MouseEvent): void {
    event.stopPropagation();
    if (this.selectedTaskId() === task.rawId) {
      this.openTaskDetails(task);
    } else {
      this.selectTask(task);
    }
  }

  // =========================================================================
  // TASK MANAGEMENT & REPLIES IMPLEMENTATION (Dynamic App Viewer Standard)
  // =========================================================================

  private ensureAppTasksLoaded(force = false): void {
    const appId = this.currentAppId();
    if (!appId) return;
    const defId =
      this.currentAppDef()?.applicationDefinitionId ||
      this.currentAppDef()?.id ||
      '';
    const key = `${appId}:${defId}`;
    if (!force && this.tasksLoadedForAppId === key) {
      return;
    }
    this.tasksLoadedForAppId = key;
    this.fetchAppTasks();
  }

  public fetchAppTasks(): void {
    const appId = this.currentAppId();
    if (appId) {
      const defId =
        this.currentAppDef()?.applicationDefinitionId ||
        this.currentAppDef()?.id ||
        '';
      this.tasksLoadedForAppId = `${appId}:${defId}`;
    }
    this.fetchComments();
    this.fetchFeatureRequests();
  }

  public getRelatedAppIds(): string[] {
    const ids = new Set<string>();
    const appId = this.currentAppId();
    if (appId) ids.add(appId);
    const def = this.currentAppDef();
    if (def?.id) ids.add(def.id);
    if (def?.applicationDefinitionId) ids.add(def.applicationDefinitionId);
    if (def?.rootAppId) ids.add(def.rootAppId);
    return Array.from(ids);
  }

  public fetchComments(isLoadMore = false): void {
    const targetIds = this.getRelatedAppIds();
    if (!targetIds.length) return;

    if (isLoadMore) {
      if (this.isLoadingMoreComments() || !this.hasMoreComments()) return;
      this.isLoadingMoreComments.set(true);
    } else {
      this.isLoadingComments.set(true);
      this.hasMoreComments.set(false);
    }

    const currentSkip = isLoadMore ? this.comments().length : 0;
    const whereClause: any = {
      relatedEntityName: 'App',
      relatedEntityId: targetIds.length === 1 ? targetIds[0] : { inq: targetIds },
      or: [
        { parentCommentId: null },
        { parentCommentId: { exists: false } },
      ],
    };

    const filterObj = {
      where: whereClause,
      order: ['createdDate DESC'],
      limit: this.commentsPageSize,
      skip: currentSkip,
    };

    this.commentController
      .find({
        filter: JSON.stringify(filterObj),
      })
      .subscribe({
        next: (comments: Comment[]) => {
          const batch = comments || [];
          this.hasMoreComments.set(batch.length >= this.commentsPageSize);

          if (isLoadMore) {
            this.comments.update((existing) => [...existing, ...batch]);
            this.isLoadingMoreComments.set(false);
          } else {
            this.comments.set(batch);
            this.isLoadingComments.set(false);
          }

          setTimeout(() => this.recalculateBubblePositions(), 200);
        },
        error: (err) => {
          console.error('[AppHarnessViewer] Failed to fetch bug comments:', err);
          if (isLoadMore) {
            this.isLoadingMoreComments.set(false);
          } else {
            this.comments.set([]);
            this.isLoadingComments.set(false);
          }
        },
      });

    if (!isLoadMore) {
      this.commentController
        .count({
          where: JSON.stringify(whereClause),
        })
        .subscribe({
          next: (res) => {
            if (res && typeof res.count === 'number') {
              this.totalCommentsCount.set(res.count);
            }
          },
          error: () => {},
        });
    }
  }

  public fetchFeatureRequests(): void {
    const targetAppId =
      this.currentAppDef()?.applicationDefinitionId ||
      this.currentAppDef()?.id ||
      this.currentAppId();
    if (!targetAppId) return;

    this.definitionController.getAppFeatureRequests({ id: targetAppId }).subscribe({
      next: (requests) => {
        const sorted = (requests || []).sort(
          (a: any, b: any) =>
            new Date(b.createdAt || b.createdDate || 0).getTime() -
            new Date(a.createdAt || a.createdDate || 0).getTime(),
        );
        this.featureRequests.set(sorted);
        setTimeout(() => this.recalculateBubblePositions(), 200);
      },
      error: () => {
        this.featureRequests.set([]);
      },
    });
  }

  public loadMoreTasks(): void {
    this.fetchComments(true);
  }

  private startSmartTaskPolling(): void {
    this.stopSmartTaskPolling();
    this.taskSmartPollInterval = setInterval(() => {
      this.pollVisibleTasksForUpdates(false);
    }, this.SMART_POLL_INTERVAL_MS);
  }

  private stopSmartTaskPolling(): void {
    if (this.taskSmartPollInterval) {
      clearInterval(this.taskSmartPollInterval);
      this.taskSmartPollInterval = null;
    }
  }

  /**
   * Smart 2-minute poll (App Builder Preview Harness ONLY):
   * 1. Polls only `id`, `updatedDate`, `status`, `isApproved`, `replyCount`, `lastRepliedDate`
   *    for the currently visible task comments on the screen.
   * 2. Compares `status` and `updatedDate` (plus reply count / approval) against in-memory comments.
   * 3. For any comment with a difference, fetches the full comment + its replies, renews the
   *    in-memory comment, and highlights the card with an update badge and distinct background.
   */
  public async pollVisibleTasksForUpdates(isManual = false): Promise<void> {
    if (!this.isSidebarOpen() || this.activeSidebarTab() !== 'Tasks') {
      return;
    }
    if (this.isLoadingComments() || this.isSmartPollingTasks()) {
      return;
    }
    if (!isManual && typeof document !== 'undefined' && document.hidden) {
      return;
    }

    const visibleTasks = this.displayedTasks();
    const idSet = new Set<string>();
    for (const t of visibleTasks) {
      if (
        t.rawId &&
        (t.type === 'Bug' ||
          (t.rawItem as any)?.category ||
          (t.rawItem as any)?.relatedEntityName === 'App')
      ) {
        idSet.add(t.rawId);
      }
    }
    const detailTask = this.selectedTaskForDetail();
    if (detailTask?.rawId) {
      idSet.add(detailTask.rawId);
    }

    const visibleIds = Array.from(idSet);
    if (visibleIds.length === 0) {
      return;
    }

    this.isSmartPollingTasks.set(true);
    try {
      const pollFilter = {
        where: {
          relatedEntityName: 'App',
          id: visibleIds.length === 1 ? visibleIds[0] : { inq: visibleIds },
        },
        fields: {
          id: true,
          updatedDate: true,
          updatedBy: true,
          updatedById: true,
          status: true,
          isApproved: true,
          replyCount: true,
          repliesCount: true,
          lastRepliedDate: true,
        },
      };

      const polledLite = await firstValueFrom(
        this.commentController.find({
          filter: JSON.stringify(pollFilter),
        }),
      );

      const existingMap = new Map<string, Comment>();
      for (const c of this.comments()) {
        if (c.id) {
          existingMap.set(c.id, c);
        }
      }

      const changedIds: string[] = [];
      const changeMetaById: Record<
        string,
        {
          previousStatus: string;
          newStatus: string;
          previousReplyCount: number;
          previousContent: string;
          statusChanged: boolean;
          approvalChanged: boolean;
          updatedDateChanged: boolean;
        }
      > = {};

      for (const item of polledLite || []) {
        if (!item?.id) continue;
        const existing = existingMap.get(item.id);
        if (!existing) continue;

        const prevUpdatedMs = existing.updatedDate
          ? new Date(existing.updatedDate).getTime()
          : 0;
        const nextUpdatedMs = item.updatedDate
          ? new Date(item.updatedDate).getTime()
          : 0;
        const prevRepliedMs = (existing as any).lastRepliedDate
          ? new Date((existing as any).lastRepliedDate).getTime()
          : 0;
        const nextRepliedMs = (item as any).lastRepliedDate
          ? new Date((item as any).lastRepliedDate).getTime()
          : 0;

        const prevStatus = String(existing.status || 'Open').trim();
        const nextStatus = String(item.status || 'Open').trim();
        const statusChanged = prevStatus !== nextStatus;
        const updatedDateChanged = nextUpdatedMs !== prevUpdatedMs;
        const approvalChanged =
          ((item as any).isApproved ?? null) !== ((existing as any).isApproved ?? null);
        const prevReplyCount =
          (existing as any).replyCount ?? (existing as any).repliesCount ?? 0;
        const nextReplyCount =
          (item as any).replyCount ?? (item as any).repliesCount;
        const replyCountChanged =
          typeof nextReplyCount === 'number' &&
          nextReplyCount !== prevReplyCount;
        const lastRepliedChanged =
          nextRepliedMs > 0 && nextRepliedMs !== prevRepliedMs;

        if (
          statusChanged ||
          updatedDateChanged ||
          approvalChanged ||
          replyCountChanged ||
          lastRepliedChanged
        ) {
          changedIds.push(item.id);
          changeMetaById[item.id] = {
            previousStatus: prevStatus,
            newStatus: nextStatus,
            previousReplyCount: prevReplyCount,
            previousContent: String(existing.content || ''),
            statusChanged,
            approvalChanged,
            updatedDateChanged,
          };
        }
      }

      if (changedIds.length === 0) {
        return;
      }

      // Fetch full updated comment(s) and their replies in parallel
      const [renewedComments, fetchedReplies] = await Promise.all([
        firstValueFrom(
          this.commentController.find({
            filter: JSON.stringify({
              where: {
                relatedEntityName: 'App',
                id: changedIds.length === 1 ? changedIds[0] : { inq: changedIds },
              },
            }),
          }),
        ),
        firstValueFrom(
          this.commentController.find({
            filter: JSON.stringify({
              where: {
                parentCommentId:
                  changedIds.length === 1 ? changedIds[0] : { inq: changedIds },
              },
              order: ['createdDate ASC'],
            }),
          }),
        ),
      ]);

      const repliesByParent = new Map<string, Comment[]>();
      for (const r of fetchedReplies || []) {
        const pid = r.parentCommentId;
        if (!pid) continue;
        const list = repliesByParent.get(pid) || [];
        list.push(r);
        repliesByParent.set(pid, list);
      }

      const renewedMap = new Map<string, Comment>();
      const newUpdatesRecord: Record<
        string,
        {
          updatedAt: number;
          statusChanged: boolean;
          previousStatus?: string;
          newStatus?: string;
          hasNewReplies: boolean;
          newRepliesCount: number;
        }
      > = {};

      for (const rc of renewedComments || []) {
        if (!rc?.id) continue;
        const replies = repliesByParent.get(rc.id) || [];
        const meta = changeMetaById[rc.id];
        const computedReplyCount = Math.max(
          replies.length,
          (rc as any).replyCount ?? (rc as any).repliesCount ?? 0,
        );

        let lastReplierNames: string[] =
          (rc as any).lastReplierNames ?? (rc as any).lastRepliers ?? [];
        if (replies.length > 0) {
          const recentUniqueNames: string[] = [];
          for (let i = replies.length - 1; i >= 0; i--) {
            const reply = replies[i];
            const isAi = this.isAiReply(reply);
            const name = isAi
              ? 'Rapider AI'
              : (
                  reply.createdBy ||
                  (reply as any).authorName ||
                  (reply as any).createdByPersonName ||
                  'User'
                ).trim();
            if (name && !recentUniqueNames.includes(name)) {
              recentUniqueNames.push(name);
            }
            if (recentUniqueNames.length >= 3) break;
          }
          lastReplierNames = recentUniqueNames.reverse();
        }

        const enriched: Comment = {
          ...rc,
          replyCount: computedReplyCount,
          repliesCount: computedReplyCount,
          lastReplierNames,
          lastRepliers: lastReplierNames,
        } as Comment;

        renewedMap.set(rc.id, enriched);

        // Determine if this update was made by AI or another user (NEVER highlight logged-in user's own changes)
        const prevRepliesCount = meta?.previousReplyCount ?? 0;
        const newRepliesSlice =
          replies.length > prevRepliesCount ? replies.slice(prevRepliesCount) : [];
        const externalNewReplies = newRepliesSlice.filter(
          (r) =>
            this.isAiReply(r) ||
            !this.isCurrentUserActor(r.createdById, r.createdBy),
        );
        const hasExternalNewReply = externalNewReplies.length > 0;

        const contentChanged =
          meta !== undefined &&
          String(rc.content || '') !== meta.previousContent;
        const meaningfulFieldChanged = Boolean(
          meta?.statusChanged || meta?.approvalChanged || contentChanged,
        );

        const nextNormStatus = String(rc.status || 'Open').trim().toLowerCase();
        const isAiDrivenStatus =
          nextNormStatus === 'in progress' ||
          nextNormStatus === 'in_progress' ||
          nextNormStatus === 'in review' ||
          nextNormStatus === 'in_review' ||
          nextNormStatus === 'ready to test' ||
          nextNormStatus === 'ready_to_test';

        const updaterId = (rc as any).updatedById || rc.createdById;
        const updaterName = (rc as any).updatedBy || rc.createdBy;
        const isUpdatedByCurrentUser = this.isCurrentUserActor(
          updaterId,
          updaterName,
        );

        const hasExternalFieldChange =
          meaningfulFieldChanged &&
          (isAiDrivenStatus || hasExternalNewReply || !isUpdatedByCurrentUser);

        if (hasExternalNewReply || hasExternalFieldChange) {
          newUpdatesRecord[rc.id] = {
            updatedAt: Date.now(),
            statusChanged: Boolean(meta?.statusChanged),
            previousStatus: meta?.previousStatus,
            newStatus: String(rc.status || 'Open'),
            hasNewReplies: hasExternalNewReply,
            newRepliesCount: externalNewReplies.length,
          };
        }
      }

      if (renewedMap.size > 0) {
        this.comments.update((list) =>
          list.map((c) => (c.id && renewedMap.has(c.id) ? renewedMap.get(c.id)! : c)),
        );

        this.recentlyUpdatedTasks.update((curr) => ({
          ...curr,
          ...newUpdatesRecord,
        }));

        const openDetail = this.selectedTaskForDetail();
        if (openDetail?.rawId && renewedMap.has(openDetail.rawId)) {
          const freshReplies = repliesByParent.get(openDetail.rawId);
          if (freshReplies) {
            this.taskReplies.set(freshReplies);
          }
          const refreshedTask = this.unifiedTasks().find(
            (t) => t.rawId === openDetail.rawId,
          );
          if (refreshedTask) {
            this.selectedTaskForDetail.set(refreshedTask);
          }
        }
      }
    } catch (err) {
      console.warn('[AppHarnessViewer] Smart task poll failed:', err);
    } finally {
      this.isSmartPollingTasks.set(false);
    }
  }

  public isTaskRecentlyUpdated(rawId: string | null | undefined): boolean {
    if (!rawId) return false;
    return Boolean(this.recentlyUpdatedTasks()[rawId]);
  }

  public getTaskUpdateInfo(rawId: string | null | undefined): {
    updatedAt: number;
    statusChanged: boolean;
    previousStatus?: string;
    newStatus?: string;
    hasNewReplies: boolean;
    newRepliesCount: number;
  } | null {
    if (!rawId) return null;
    return this.recentlyUpdatedTasks()[rawId] || null;
  }

  public dismissTaskUpdate(rawId: string, e?: Event): void {
    if (e) e.stopPropagation();
    if (!rawId || !this.recentlyUpdatedTasks()[rawId]) return;
    this.recentlyUpdatedTasks.update((curr) => {
      const next = { ...curr };
      delete next[rawId];
      return next;
    });
  }

  public clearAllTaskUpdates(e?: Event): void {
    if (e) e.stopPropagation();
    this.recentlyUpdatedTasks.set({});
  }

  private isAiReply(reply: any): boolean {
    if (!reply) return false;
    return (
      reply.isAi === true ||
      reply.createdById === '00000000-0000-0000-0000-000000000001' ||
      reply.createdBy === 'Rapider AI' ||
      reply.authorName === 'Rapider AI' ||
      Boolean(reply.content?.startsWith('### 🤖 AI Agent')) ||
      Boolean(reply.content?.includes('🤖 AI Agent Implementation Report'))
    );
  }

  /**
   * Returns true if the given actor ID or name belongs to the currently logged-in human user.
   * Always returns false for Rapider AI.
   */
  private isCurrentUserActor(
    actorId?: string | null,
    actorName?: string | null,
  ): boolean {
    if (
      actorId === '00000000-0000-0000-0000-000000000001' ||
      (actorName && actorName.trim().toLowerCase() === 'rapider ai')
    ) {
      return false;
    }
    const person = this.authStore.activePerson() as any;
    const user = this.authStore.user() as any;
    const currentPersonId = person?.id;
    const currentUserId = user?.id || user?.userId;
    const currentEmail = String(user?.email || user?.username || '')
      .trim()
      .toLowerCase();
    const currentNames = new Set<string>(
      [
        person?.fullName,
        person?.firstName
          ? `${person.firstName} ${person.lastName || ''}`.trim()
          : '',
        user?.personFullName,
        user?.fullName,
        user?.name,
        user?.username,
        this.formatAuthorName(currentEmail),
      ]
        .filter(Boolean)
        .map((n: string) => n.trim().toLowerCase()),
    );

    if (actorId && (actorId === currentPersonId || actorId === currentUserId)) {
      return true;
    }
    const cleanActorName = String(actorName || '').trim().toLowerCase();
    if (cleanActorName) {
      if (currentNames.has(cleanActorName)) return true;
      if (currentEmail && cleanActorName === currentEmail) return true;
    }
    return false;
  }

  /**
   * Syncs the server's updated `updatedDate` / `updatedById` / `replyCount` into the local
   * in-memory `comments` signal after the logged-in user performs an action on a task,
   * ensuring the user's own action never triggers a green card highlight on the next poll.
   */
  private syncCommentAfterSelfMutation(commentId: string): void {
    if (!commentId) return;
    this.dismissTaskUpdate(commentId);
    this.commentController
      .find({
        filter: JSON.stringify({
          where: {
            relatedEntityName: 'App',
            id: commentId,
          },
          fields: {
            id: true,
            updatedDate: true,
            updatedBy: true,
            updatedById: true,
            status: true,
            isApproved: true,
            replyCount: true,
            repliesCount: true,
            lastRepliedDate: true,
          },
        }),
      })
      .subscribe({
        next: (res) => {
          const fresh = res?.[0];
          if (!fresh?.id) return;
          this.comments.update((list) =>
            list.map((c) =>
              c.id === fresh.id
                ? ({
                    ...c,
                    updatedDate: fresh.updatedDate ?? c.updatedDate,
                    updatedBy: (fresh as any).updatedBy ?? (c as any).updatedBy,
                    updatedById:
                      (fresh as any).updatedById ?? (c as any).updatedById,
                    lastRepliedDate:
                      (fresh as any).lastRepliedDate ??
                      (c as any).lastRepliedDate,
                  } as Comment)
                : c,
            ),
          );
        },
        error: () => {},
      });
  }

  public selectTask(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    this.selectedTaskId.set(task.rawId);
    this.ensureTasksSidebarOpen();
    setTimeout(() => {
      const cardEl =
        document.getElementById('task-card-' + task.rawId) ||
        (document.querySelector(`[data-task-id="${task.rawId}"]`) as HTMLElement);
      if (cardEl) {
        cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 50);
  }

  public openTaskDetails(task: UnifiedTaskItem): void {
    this.dismissTaskUpdate(task.rawId);
    this.selectedTaskForDetail.set(task);
    this.selectedTaskId.set(task.rawId);
    this.triageNote.set('');
    this.isEditingTaskDetail.set(false);
    this.loadTaskReplies(task.rawId);
  }

  public closeTaskDetailDrawer(): void {
    this.selectedTaskForDetail.set(null);
    this.taskReplies.set([]);
    this.replyContent.set('');
    this.triageNote.set('');
    this.isEditingTaskDetail.set(false);
  }

  /** Optional note entered by Owner/Admin when closing or triaging a task in 1 click */
  public triageNote = signal<string>('');

  /**
   * RPDR-613: Reporter can edit Title, Description, and Type (Bug / Feature) while task status is Open.
   */
  public canEditTask(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    if (task.isApproved === true) return false;
    if (this.getNormalizedTaskStatus(task) !== 'Open') return false;
    return this.isTaskReporter(task);
  }

  public startEditingTask(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    if (!this.canEditTask(task)) return;
    if (this.selectedTaskForDetail()?.rawId !== task.rawId) {
      this.openTaskDetails(task);
    }
    this.editTaskTitle.set(task.title || '');
    this.editTaskDescription.set(task.description || '');
    this.editTaskType.set(task.type === 'Feature' ? 'Feature' : 'Bug');
    this.isEditingTaskDetail.set(true);
  }

  public cancelEditingTask(): void {
    this.isEditingTaskDetail.set(false);
  }

  public async saveTaskEdit(task: UnifiedTaskItem): Promise<void> {
    if (!task || !this.canEditTask(task)) return;
    const title = this.editTaskTitle().trim();
    const description = this.editTaskDescription().trim();
    const newType = this.editTaskType();
    if (!title) return;

    this.isSavingTaskEdit.set(true);
    try {
      const newCategory = newType === 'Feature' ? 'Feature Request' : 'Bug Report';
      const newContent = description ? `${title}\n\n${description}` : title;
      const raw = (task.rawItem as any) || {};
      const existingMetaArr: Array<{ key: string; value: any }> = Array.isArray(raw.metadata)
        ? raw.metadata.filter((m: any) => m && m.key && m.key !== 'title' && m.key !== 'description')
        : [];
      const updatedMetadata: Array<{ key: string; value: any }> = [
        { key: 'title', value: title },
        ...(description ? [{ key: 'description', value: description }] : []),
        ...existingMetaArr,
      ];

      let savedContent = newContent;
      try {
        await firstValueFrom(
          this.commentController.updateById({
            id: task.rawId,
            body: {
              content: newContent,
              category: newCategory,
              metadata: updatedMetadata,
            } as any,
          }),
        );
      } catch (primaryErr: any) {
        if (primaryErr?.status === 422) {
          savedContent = `[${newType}] ${newContent}`;
          await firstValueFrom(
            this.commentController.updateById({
              id: task.rawId,
              body: {
                content: savedContent,
              } as any,
            }),
          );
        } else {
          throw primaryErr;
        }
      }

      this.comments.update((list) =>
        list.map((c) =>
          c.id === task.rawId
            ? ({
                ...c,
                content: savedContent,
                category: newCategory as any,
                metadata: updatedMetadata as any,
              } as Comment)
            : c,
        ),
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((curr) =>
          curr
            ? {
                ...curr,
                title,
                description,
                type: newType,
                rawItem: {
                  ...(curr.rawItem || {}),
                  content: newContent,
                  category: newCategory,
                  metadata: updatedMetadata,
                },
              }
            : null,
        );
      }

      this.isEditingTaskDetail.set(false);
      this.syncCommentAfterSelfMutation(task.rawId);
      this.notificationService.createNotification(
        'success',
        'Task Updated',
        `Task ${task.id} has been updated.`,
      );
    } catch (err: any) {
      console.error('[AppHarnessViewer] Failed to update task:', err);
      this.notificationService.createNotification(
        'error',
        'Update Failed',
        err?.error?.error?.message || err?.message || 'Failed to save task changes.',
      );
    } finally {
      this.isSavingTaskEdit.set(false);
    }
  }

  public loadTaskReplies(taskId: string): void {
    if (!taskId) {
      this.taskReplies.set([]);
      return;
    }
    this.isLoadingReplies.set(true);
    this.commentController
      .find({
        filter: {
          where: { parentCommentId: taskId },
          order: ['createdDate ASC'],
        },
      })
      .subscribe({
        next: (replies) => {
          this.taskReplies.set(replies || []);
          this.isLoadingReplies.set(false);
        },
        error: (err) => {
          console.error('[AppHarnessViewer] Failed to load task replies:', err);
          this.taskReplies.set([]);
          this.isLoadingReplies.set(false);
        },
      });
  }

  public async submitTaskReply(): Promise<void> {
    const text = this.replyContent().trim();
    const task = this.selectedTaskForDetail();
    if (!text || !task || !task.rawId) return;

    this.isSubmittingReply.set(true);
    try {
      const createdReply = await firstValueFrom(
        this.commentController.reply({
          parentCommentId: task.rawId,
          body: { content: text },
        }),
      );
      this.taskReplies.update((list) => [...list, createdReply]);
      // Optimistically update parent comment's reply stats in the local comments list
      const activeP = this.authStore.activePerson() as any;
      const activeU = this.authStore.user() as any;
      const authorName = activeP?.fullName || activeU?.firstName || 'User';
      this.comments.update((items) =>
        items.map((c) => {
          if (c.id === task.rawId) {
            const currentCount = ((c as any).replyCount ?? (c as any).repliesCount ?? 0) + 1;
            const existingNames = [...((c as any).lastReplierNames ?? (c as any).lastRepliers ?? [])];
            if (!existingNames.includes(authorName)) {
              existingNames.push(authorName);
            }
            const updatedNames = existingNames.slice(-3);
            return {
              ...c,
              replyCount: currentCount,
              repliesCount: currentCount,
              lastReplierNames: updatedNames,
              lastRepliers: updatedNames,
            };
          }
          return c;
        })
      );
      this.replyContent.set('');
      this.syncCommentAfterSelfMutation(task.rawId);
      this.notificationService.createNotification(
        'success',
        'Reply Posted',
        'Your comment reply has been posted successfully.',
      );
    } catch (err: any) {
      console.error('[AppHarnessViewer] Failed to submit reply:', err);
      this.notificationService.createNotification(
        'error',
        'Failed to Post Reply',
        err.message || 'Could not post reply.',
      );
    } finally {
      this.isSubmittingReply.set(false);
    }
  }

  /**
   * Checks whether the current user is the original creator / reporter of this task.
   */
  public isTaskReporter(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    const person = this.authStore.activePerson();
    const user = this.authStore.user();
    const currentPersonId = person?.id;
    const currentUserId = (user as any)?.id;
    const currentEmail = ((user as any)?.email || (user as any)?.username || '').toLowerCase();
    const currentName = (
      person?.firstName
        ? `${person.firstName} ${person.lastName || ''}`.trim()
        : (user as any)?.username || (user as any)?.name || ''
    ).toLowerCase();

    const raw = task.rawItem as any;
    const taskCreatorId =
      task.createdById ||
      raw?.createdById ||
      raw?.createdByPersonId ||
      raw?.userId ||
      raw?.authorId;

    if (taskCreatorId && (taskCreatorId === currentPersonId || taskCreatorId === currentUserId)) {
      return true;
    }
    if (task.createdBy && currentName && task.createdBy.toLowerCase() === currentName) {
      return true;
    }
    if (task.createdBy && currentEmail && task.createdBy.toLowerCase() === currentEmail) {
      return true;
    }
    return false;
  }

  /**
   * Determines if the current user can modify approval (approve, reject, reset).
   * Rules 2 & 5:
   * - After a task is approved and status is not open any more, user cannot un-approve or reject it.
   * - Only owner and admin can reset after approved IF the status is open.
   */
  public canModifyApproval(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    if (!this.canApproveTasks()) return false;
    const norm = this.getNormalizedTaskStatus(task);
    // Triage actions apply in Open, Reopened, Accepted (pre-execution queue), and Closed states
    return norm === 'Open' || norm === 'Reopened' || norm === 'Accepted' || norm === 'Closed';
  }

  /**
   * Navigates to the page where this task/comment was created.
   */
  public navigateToTaskPage(task: UnifiedTaskItem | null): void {
    if (!task) return;
    const pageId = this.getTaskPageIdentifier(task);
    if (pageId) {
      this.navigateToPage(pageId);
    }
  }

  /**
   * Verifies that the current user is authorized to open the App Builder Preview Harness
   * (App Creator/Owner, Admin, `app:approve-request`, or builder in the App's owning workspace).
   */
  public canAccessPreviewHarness(appDefInput?: any): boolean {
    if (this.canApproveTasks()) {
      return true;
    }
    const appDef = appDefInput || this.currentAppDef();
    if (!appDef) return false;

    const person = this.authStore.activePerson();
    const user = this.authStore.user();
    const activeProject = this.authStore.activeProject() as any;
    const currentPersonId = person?.id;
    const currentUserId = (user as any)?.id;

    const ideaCreatorId = appDef?.value?.appIdea?.createdById;
    if (ideaCreatorId && (ideaCreatorId === currentPersonId || ideaCreatorId === currentUserId)) {
      return true;
    }

    const storePermissions: string[] = this.authStore.permissions() || [];
    const userPermissions: string[] = (user as any)?.permissions || [];
    const userScope: string[] = (user as any)?.scope || [];
    const userRoles = (user as any)?.roles || (this.authStore.roles() || []);

    const allAuthStrings = [
      ...storePermissions,
      ...userPermissions,
      ...userScope,
      ...(Array.isArray(userRoles)
        ? userRoles.map((r: any) =>
            typeof r === 'string' ? r : r?.name || r?.title || r?.key || '',
          )
        : []),
    ].map((s) => (typeof s === 'string' ? s.trim().toLowerCase() : ''));

    const hasWorkspaceAppPerms =
      allAuthStrings.includes('app-builder:read-workspace-apps') ||
      allAuthStrings.includes('app-builder:read') ||
      allAuthStrings.includes('app:read') ||
      allAuthStrings.includes('application:read') ||
      allAuthStrings.includes('admin') ||
      allAuthStrings.includes('superadmin') ||
      allAuthStrings.includes('owner') ||
      allAuthStrings.includes('app-builder');

    const currentProjectId =
      activeProject?.id ||
      (user as any)?.['lastProjectId'] ||
      this.browserStorage.getItem<string>('activeProjectId') ||
      this.browserStorage.getItem<string>('projectId');

    // If the user has workspace app read permissions or admin rights:
    if (hasWorkspaceAppPerms) {
      if (!appDef.projectId || !currentProjectId || appDef.projectId === currentProjectId) {
        return true;
      }
      return true;
    }

    // If the app belongs to the user's currently active workspace and they own that workspace
    if (appDef?.projectId && currentProjectId && appDef.projectId === currentProjectId) {
      if (currentPersonId && activeProject?.userId === currentPersonId) {
        return true;
      }
    }

    return false;
  }

  /**
   * Checks if current user can mark the task as Resolved / Confirmed:
   * - 'In Review' (QA Verification) -> strictly App Owner or Admin (`canApproveTasks()`).
   * - 'Released' (Reporter Confirmation) -> Reporter, App Owner, or Admin.
   */
  public canMarkResolved(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    const s = this.getNormalizedTaskStatus(task);
    if (s === 'In Review') {
      if (!this.hasDevelopBuild() || !this.isTaskOnActivePage(task)) return false;
      return this.canApproveTasks();
    }
    if (s === 'Released') {
      return this.isTaskReporter(task) || this.canApproveTasks();
    }
    return false;
  }

  /**
   * Checks if current user can mark the task as Not Resolved / Reopened:
   * - 'In Review' (QA Verification) -> strictly App Owner or Admin (`canApproveTasks()`).
   * - 'Released' (Reporter Confirmation) -> Reporter, App Owner, or Admin.
   */
  public canMarkNotResolved(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    const s = this.getNormalizedTaskStatus(task);
    if (s === 'In Review') {
      if (!this.hasDevelopBuild() || !this.isTaskOnActivePage(task)) return false;
      return this.canApproveTasks();
    }
    if (s === 'Released') {
      return this.isTaskReporter(task) || this.canApproveTasks();
    }
    return false;
  }

  /**
   * User marks task as Resolved:
   * When 'In Review' (QA Verification) -> transitions to 'Ready for Release'.
   * When 'Released' (Reporter Confirmation) -> transitions to 'Confirmed'.
   */
  public async handleMarkResolved(task: UnifiedTaskItem, e?: Event): Promise<void> {
    if (e) e.stopPropagation();
    const norm = this.getNormalizedTaskStatus(task);
    if (norm === 'In Review' && !this.hasDevelopBuild()) {
      this.notificationService.createNotification(
        'warning',
        'Develop Build Required',
        'Please compile the latest develop build before marking this task as Resolved.'
      );
      return;
    }
    if (norm === 'In Review' && !this.isTaskOnActivePage(task)) {
      this.notificationService.createNotification(
        'warning',
        'Page Switch Required',
        `Please navigate to the '${this.getTaskPageName(task)}' page to verify this resolution.`
      );
      return;
    }
    if (!this.canMarkResolved(task)) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        norm === 'In Review'
          ? 'Only the application owner or an administrator can verify QA tasks.'
          : 'Only the task reporter, application owner, or administrator can confirm this task.'
      );
      return;
    }
    const targetStatus = norm === 'Released' ? 'Confirmed' : 'Ready for Release';
    await this.setTaskStatus(task, targetStatus, e);
  }

  /**
   * Opens the rejection/reopen reason modal when user clicks "Not Resolved" or "Reopen".
   */
  public openRejectModal(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    const norm = this.getNormalizedTaskStatus(task);
    if (norm === 'In Review' && !this.hasDevelopBuild()) {
      this.notificationService.createNotification(
        'warning',
        'Develop Build Required',
        'Please compile the latest develop build before marking this task as Not Resolved.'
      );
      return;
    }
    if (norm === 'In Review' && !this.isTaskOnActivePage(task)) {
      this.notificationService.createNotification(
        'warning',
        'Page Switch Required',
        `Please navigate to the '${this.getTaskPageName(task)}' page to verify this resolution.`
      );
      return;
    }
    if (!this.canMarkNotResolved(task)) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        norm === 'In Review'
          ? 'Only the application owner or an administrator can reject QA verification.'
          : 'Only the task reporter, application owner, or administrator can reopen this task.'
      );
      return;
    }
    this.taskPendingRejection.set(task);
    this.rejectionReason.set('');
    this.isRejectModalVisible.set(true);
  }

  public closeRejectModal(): void {
    this.isRejectModalVisible.set(false);
    this.taskPendingRejection.set(null);
    this.rejectionReason.set('');
  }

  /**
   * Confirms solution rejection ('In Review' -> 'Open') or reporter reopen ('Released' -> 'Reopened').
   */
  public async confirmRejectSolution(): Promise<void> {
    const task = this.taskPendingRejection();
    const reason = this.rejectionReason().trim();
    if (!task || !task.rawId || !reason) return;

    const norm = this.getNormalizedTaskStatus(task);
    const isReleasedReopen = norm === 'Released';
    const targetStatus = isReleasedReopen ? 'Reopened' : 'Open';
    this.isSubmittingRejection.set(true);
    const formattedReply = isReleasedReopen
      ? `Task reopened by reporter with the reason below:\n\n${reason}`
      : `Solution is rejected with the reason below:\n\n${reason}`;

    try {
      // 1. Post rejection/reopen reason as reply
      const createdReply = await firstValueFrom(
        this.commentController.reply({
          parentCommentId: task.rawId,
          body: { content: formattedReply },
        })
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.taskReplies.update((list) => [...list, createdReply]);
      }

      // 2. Reset task status and approval
      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        try {
          await firstValueFrom(
            this.commentController.updateById({
              id: task.rawId,
              body: { status: targetStatus as any, isApproved: null } as any,
            })
          );
        } catch (patchErr: any) {
          if (patchErr?.status === 403) {
            await firstValueFrom(
              this.commentController.updateById({
                id: task.rawId,
                body: { status: targetStatus as any } as any,
              })
            );
          } else {
            throw patchErr;
          }
        }
      } else {
        const appId = this.currentAppId();
        if (appId) {
          await firstValueFrom(
            this.definitionController.updateAppFeatureRequestStatus({
              id: appId,
              requestId: task.rawId,
              body: { status: targetStatus as any },
            })
          );
        }
      }

      // 3. Optimistic local state update
      task.status = targetStatus;
      task.isApproved = null;

      this.comments.update((list) =>
        list.map((c) => {
          if (c.id === task.rawId) {
            return { ...c, status: targetStatus as any, isApproved: null };
          }
          return c;
        })
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((current) =>
          current ? { ...current, status: targetStatus, isApproved: null } : null
        );
      }

      this.closeRejectModal();
      this.syncCommentAfterSelfMutation(task.rawId);

      this.notificationService.createNotification(
        'info',
        targetStatus === 'Reopened' ? 'Task Reopened' : 'Solution Rejected',
        targetStatus === 'Reopened'
          ? 'Rejection reason recorded and task reopened for triage.'
          : 'Rejection reason recorded and task reopened. Requires Owner/Admin approval before re-execution.'
      );
    } catch (err: any) {
      console.error('[AppHarnessViewer] Failed to reject solution:', err);
      this.notificationService.createNotification(
        'error',
        'Rejection Failed',
        err.message || 'Could not record rejection reason.'
      );
    } finally {
      this.isSubmittingRejection.set(false);
    }
  }

  /**
   * User marks task as Not Resolved (delegates to openRejectModal to capture explanation)
   */
  public async handleMarkNotResolved(task: UnifiedTaskItem, e?: Event): Promise<void> {
    this.openRejectModal(task, e);
  }

  public async setTaskStatus(task: UnifiedTaskItem, newStatus: string, e?: Event): Promise<void> {
    if (e) e.stopPropagation();
    if (!task || !task.rawId) return;

    // Reporter can mark as Confirmed or Reopened only when task is in Released status
    const isReporterAction =
      (newStatus === 'Confirmed' || newStatus === 'Reopened' || newStatus === 'Resolved' || newStatus === 'Reporter Rejected') &&
      this.isTaskReporter(task);

    if (newStatus === 'Confirmed' || newStatus === 'Reopened' || newStatus === 'Resolved' || newStatus === 'Reporter Rejected') {
      if (task.status !== 'Released' && this.getNormalizedTaskStatus(task) !== 'Released') {
        this.notificationService.createNotification(
          'warning',
          'Release Required',
          'Reporter verification can only be performed after a version is released and the task is in Released status.'
        );
        return;
      }
    }

    if (!this.canApproveTasks() && !isReporterAction && !this.isTaskReporter(task)) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        'Only application owners and administrators, or the task reporter, can change workflow status.'
      );
      return;
    }

    try {
      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        await firstValueFrom(
          this.commentController.updateById({
            id: task.rawId,
            body: { status: newStatus as any },
          }),
        );
      } else {
        const appId = this.currentAppId();
        if (appId) {
          await firstValueFrom(
            this.definitionController.updateAppFeatureRequestStatus({
              id: appId,
              requestId: task.rawId,
              body: { status: newStatus as any },
            }),
          );
        }
      }

      task.status = newStatus;

      this.comments.update((list) =>
        list.map((c) => {
          if (c.id === task.rawId) {
            return { ...c, status: newStatus as any };
          }
          return c;
        }),
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((current) =>
          current ? { ...current, status: newStatus } : null,
        );
      }

      this.syncCommentAfterSelfMutation(task.rawId);

      this.notificationService.createNotification(
        'success',
        'Status Updated',
        `Task status changed to "${newStatus}".`,
      );
    } catch (err: any) {
      console.error('[AppHarnessViewer] Failed to update task status:', err);
      this.notificationService.createNotification(
        'error',
        'Update Failed',
        err.message || 'Could not update task status.',
      );
    }
  }

  public async setTaskApproval(task: UnifiedTaskItem, approved: boolean | null, e?: Event): Promise<void> {
    if (e) e.stopPropagation();
    if (!this.canApproveTasks()) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        'Only application owners and authorized administrators can approve or reject requests.',
      );
      return;
    }

    // Once approved and actively executing or beyond (not Open / Accepted), cannot un-approve or close
    const currentNorm = this.getNormalizedTaskStatus(task);
    if (task.isApproved === true && approved !== true) {
      if (currentNorm !== 'Open' && currentNorm !== 'Accepted') {
        this.notificationService.createNotification(
          'warning',
          'Action Not Allowed',
          'This task is in progress or completed. Approval cannot be revoked once active.',
        );
        return;
      }
    }

    try {
      let updatedStatus = task.status;
      if (approved === true) {
        updatedStatus = 'Accepted';
      } else if (approved === false) {
        updatedStatus = 'Closed';
      } else if (approved === null) {
        updatedStatus = 'Open';
      }

      const updatePayload: any = { isApproved: approved };
      if (updatedStatus !== task.status) {
        updatePayload.status = updatedStatus;
      }

      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        await firstValueFrom(
          this.commentController.updateById({
            id: task.rawId,
            body: updatePayload,
          }),
        );

        const optionalNote =
          this.selectedTaskForDetail()?.rawId === task.rawId
            ? this.triageNote().trim()
            : '';
        if (optionalNote) {
          try {
            const actionLabel =
              approved === false
                ? '**Task Closed:**'
                : approved === true
                  ? '**Task Approved:**'
                  : '**Task Reopened:**';
            const createdReply = await firstValueFrom(
              this.commentController.reply({
                parentCommentId: task.rawId,
                body: { content: `${actionLabel} ${optionalNote}` },
              }),
            );
            this.taskReplies.update((list) => [...list, createdReply]);
            this.triageNote.set('');
          } catch (replyErr) {
            console.warn('[AppHarnessViewer] Optional triage note failed to post:', replyErr);
          }
        }
      }

      task.isApproved = approved;
      task.status = updatedStatus;

      this.comments.update((list) =>
        list.map((c) => {
          if (c.id === task.rawId) {
            return { ...c, isApproved: approved, status: updatedStatus as any };
          }
          return c;
        }),
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((current) =>
          current ? { ...current, isApproved: approved, status: updatedStatus } : null,
        );
      }

      this.syncCommentAfterSelfMutation(task.rawId);

      let title = 'Status Updated';
      let message = 'Approval status updated successfully.';
      let type: 'success' | 'warning' | 'info' = 'info';

      if (approved === true) {
        title = 'Task Approved';
        message = 'This request is now approved and eligible for automated AI execution.';
        type = 'success';
      } else if (approved === false) {
        title = 'Task Closed';
        message = 'This task has been closed.';
        type = 'info';
      } else {
        title = 'Task Reopened';
        message = 'This task has been reopened and returned to Open status.';
        type = 'info';
      }

      this.notificationService.createNotification(type, title, message);
    } catch (err: any) {
      console.error('Failed to update task approval:', err);
      this.notificationService.createNotification(
        'error',
        'Approval Failed',
        err.message || 'Could not update approval status.',
      );
    }
  }

  public async toggleTaskApproval(task: UnifiedTaskItem, e?: Event): Promise<void> {
    const next = task.isApproved === true ? false : true;
    return this.setTaskApproval(task, next, e);
  }

  public confirmDeleteTask(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    this.taskToDelete.set(task);
  }

  public async executeDeleteTask(): Promise<void> {
    const task = this.taskToDelete();
    if (!task) return;

    const appId = this.currentAppId();

    try {
      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        await firstValueFrom(
          this.commentController.deleteById({ id: task.rawId }),
        );
        this.comments.update((list) =>
          list.filter((c) => c.id !== task.rawId),
        );
        this.totalCommentsCount.update((count) =>
          count !== null && count > 0 ? count - 1 : null,
        );
      } else {
        if (appId) {
          try {
            await firstValueFrom(
              this.definitionController.deleteById({ id: task.rawId }),
            );
          } catch {
            await firstValueFrom(
              this.definitionController.updateAppFeatureRequestStatus({
                id: appId,
                requestId: task.rawId,
                body: { status: 'Declined' },
              }),
            );
          }
          this.featureRequests.update((list) =>
            list.filter((r) => r.id !== task.rawId),
          );
        }
      }

      this.notificationService.createNotification(
        'success',
        'Task Deleted',
        `Task ${task.id} was deleted successfully.`,
      );

      if (this.selectedTaskId() === task.rawId) {
        this.selectedTaskId.set(null);
      }
      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.set(null);
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to delete task.',
      );
    } finally {
      this.taskToDelete.set(null);
    }
  }

  public startReportBug(): void {
    this.draftTaskType.set('Bug');
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
    this.pinnedMetadata.set(null);
    this.isPinningFromModal = false;
    this.setCommentMode(true);
    this.notificationService.createNotification(
      'info',
      'Drop Bug Bubble',
      'Click any element on the micro app canvas to drop a bug marker.',
    );
  }

  public openRequestFeatureModal(): void {
    this.draftTaskType.set('Feature');
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
    this.pinnedMetadata.set(null);
    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(true);
  }

  public closeCreateTaskModal(): void {
    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(false);
    this.setCommentMode(false);
    this.pinnedMetadata.set(null);
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
  }

  public async handleCreateTaskSubmit(): Promise<void> {
    const appId = this.currentAppId();
    const title = this.draftTaskTitle().trim();
    const description = this.draftTaskDescription().trim();
    const type = this.draftTaskType();
    const pin = this.pinnedMetadata();

    if (!appId || !title) return;

    this.isSubmittingTask.set(true);

    try {
      const user = this.authStore.user();
      const content = description ? `${title}\n\n${description}` : title;
      const targetAppId =
        this.currentAppDef()?.applicationDefinitionId ||
        this.currentAppDef()?.id ||
        appId;
      const activePage = this.activePageRoute() || this.activePageId() || '';
      const metadata: Array<{ key: string; value: string }> = [
        { key: 'title', value: title },
        ...(description ? [{ key: 'description', value: description }] : []),
        { key: 'pageId', value: activePage },
        { key: 'appId', value: targetAppId },
      ];
      if (pin) {
        if (pin.elementId) metadata.push({ key: 'elementId', value: pin.elementId });
        if (pin.uniqueSelector) metadata.push({ key: 'uniqueSelector', value: pin.uniqueSelector });
        if (pin.offsetX !== undefined) metadata.push({ key: 'offsetX', value: pin.offsetX.toString() });
        if (pin.offsetY !== undefined) metadata.push({ key: 'offsetY', value: pin.offsetY.toString() });
        if (pin.absoluteX !== undefined) metadata.push({ key: 'absoluteX', value: pin.absoluteX.toString() });
        if (pin.absoluteY !== undefined) metadata.push({ key: 'absoluteY', value: pin.absoluteY.toString() });
        if (pin.tag) metadata.push({ key: 'tag', value: pin.tag });
      }

      const category = type === 'Feature' ? 'Feature Request' : 'Bug Report';

      const newComment: Omit<Comment, 'id'> = {
        content: content,
        category: category,
        status: 'Open',
        projectId: (user as any)?.['lastProjectId'] || '',
        relatedEntityName: 'App',
        relatedEntityId: targetAppId,
        metadata: metadata,
      };

      const created = await firstValueFrom(
        this.commentController.create({ body: newComment as any }),
      );
      this.comments.update((c) => [created, ...c]);
      this.notificationService.createNotification(
        'success',
        type === 'Feature' ? 'Feature Requested' : 'Bug Reported',
        `Successfully logged ${type === 'Feature' ? 'feature request' : 'bug'} "${title.slice(0, 25)}..."`,
      );

      this.isCreateTaskModalVisible.set(false);
      this.draftTaskTitle.set('');
      this.draftTaskDescription.set('');
      this.pinnedMetadata.set(null);
      this.fetchAppTasks();
      setTimeout(() => this.recalculateBubblePositions(), 300);
    } catch (err) {
      console.error('Failed to create task:', err);
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to create task.',
      );
    } finally {
      this.isSubmittingTask.set(false);
    }
  }

  public startFeatureBubblePin(): void {
    this.isPinningFromModal = true;
    this.isCreateTaskModalVisible.set(false);
    this.setCommentMode(true);
    this.notificationService.createNotification(
      'info',
      'Drop Element Pin',
      'Click any element on the preview canvas to attach it to this task.',
    );
  }

  public toggleTaskDescription(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    this.expandedTaskDescriptions.update((map) => ({
      ...map,
      [task.rawId]: !map[task.rawId],
    }));
  }

  public isTaskDescriptionExpanded(rawId: string): boolean {
    return !!this.expandedTaskDescriptions()[rawId];
  }

  public isDescriptionLong(desc?: string): boolean {
    if (!desc) return false;
    return desc.length > 120 || desc.includes('\n');
  }

  public toggleReportedByMeFilter(e?: Event): void {
    if (e) e.stopPropagation();
    this.reportedByMeOnly.update((v) => !v);
    this.loadedTasksLimit.set(20);
  }

  public clearReportedByMeFilter(): void {
    this.reportedByMeOnly.set(false);
    this.loadedTasksLimit.set(20);
  }

  public selectAllStatusesFilter(e?: Event): void {
    if (e) e.stopPropagation();
    this.selectedTaskStatuses.set([]);
    this.taskStatusFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public toggleTaskStatusCheckbox(status: string, e?: Event): void {
    if (e) e.stopPropagation();
    this.selectedTaskStatuses.update((curr) =>
      curr.includes(status) ? curr.filter((s) => s !== status) : [...curr, status],
    );
    this.taskStatusFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public removeTaskStatusFilter(status: string): void {
    this.selectedTaskStatuses.update((curr) => curr.filter((s) => s !== status));
    if (this.taskStatusFilter() === status) {
      this.taskStatusFilter.set('ALL');
    }
    this.loadedTasksLimit.set(20);
  }

  public clearSearchFilter(): void {
    this.taskSearchQuery.set('');
    this.loadedTasksLimit.set(20);
  }

  public clearTypeFilter(): void {
    this.taskTypeFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public clearStatusFilter(): void {
    this.selectedTaskStatuses.set([]);
    this.taskStatusFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public clearApprovalFilter(): void {
    this.taskApprovalFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public resetAllFilters(): void {
    this.taskSearchQuery.set('');
    this.taskTypeFilter.set('ALL');
    this.taskStatusFilter.set('ALL');
    this.selectedTaskStatuses.set([]);
    this.reportedByMeOnly.set(false);
    this.taskApprovalFilter.set('ALL');
    this.taskPageFilter.set('CURRENT');
    this.loadedTasksLimit.set(20);
  }

  public setTaskTypeFilter(type: string): void {
    this.taskTypeFilter.set(toggleFilterValue(this.taskTypeFilter(), type));
    this.loadedTasksLimit.set(20);
  }

  public setTaskStatusFilter(status: string): void {
    this.selectedTaskStatuses.set([]);
    this.taskStatusFilter.set(toggleFilterValue(this.taskStatusFilter(), status));
    this.loadedTasksLimit.set(20);
  }

  public setTaskApprovalFilter(filter: 'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'): void {
    this.taskApprovalFilter.set(filter);
    this.loadedTasksLimit.set(20);
  }

  public formatReplierNames(names?: string[]): string {
    if (!names || !names.length) return '';
    return names.join(', ');
  }

  public formatAuthorName(name: string | undefined): string {
    if (!name) return 'Anonymous User';
    if (name.includes('@')) {
      const parts = name.split('@');
      const localPart = parts[0];
      return localPart
        .split('.')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    }
    return name;
  }

  public formatTaskDate(dateStr: string | Date | undefined): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  public getInitials(name: string | undefined): string {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  public extractAvatarUrl(item: any): string | undefined {
    if (!item) return undefined;
    if (typeof item.authorAvatarUrl === 'string' && item.authorAvatarUrl.trim()) {
      return item.authorAvatarUrl.trim();
    }
    if (typeof item.authorAvatar === 'string' && item.authorAvatar.trim()) {
      return item.authorAvatar.trim();
    }
    if (typeof item.avatarUrl === 'string' && item.avatarUrl.trim()) {
      return item.avatarUrl.trim();
    }
    if (typeof item.avatar === 'string' && item.avatar.trim()) {
      return item.avatar.trim();
    }
    if (typeof item.photoUrl === 'string' && item.photoUrl.trim()) {
      return item.photoUrl.trim();
    }
    if (item.user && typeof item.user.avatarUrl === 'string' && item.user.avatarUrl.trim()) {
      return item.user.avatarUrl.trim();
    }
    if (item.user && typeof item.user.photoUrl === 'string' && item.user.photoUrl.trim()) {
      return item.user.photoUrl.trim();
    }
    if (Array.isArray(item.metadata)) {
      const found = item.metadata.find(
        (m: any) => m && (m.key === 'avatarUrl' || m.key === 'photoUrl' || m.key === 'authorAvatarUrl'),
      );
      if (found && typeof found.value === 'string' && found.value.trim()) {
        return found.value.trim();
      }
    }
    return undefined;
  }

  private parseCommentCoordinates(comment: Comment): any | null {
    if (!comment || !comment.metadata) return null;
    let rawMeta = comment.metadata as any;
    if (typeof rawMeta === 'string') {
      try {
        rawMeta = JSON.parse(rawMeta);
      } catch {
        return null;
      }
    }
    const metaObj: Record<string, any> = {};
    if (Array.isArray(rawMeta)) {
      for (const m of rawMeta) {
        if (m && m.key) metaObj[m.key] = m.value;
      }
    } else if (typeof rawMeta === 'object' && rawMeta !== null) {
      Object.assign(metaObj, rawMeta);
    } else {
      return null;
    }
    const x = parseFloat(metaObj['absoluteX'] ?? metaObj['offsetX'] ?? metaObj['x'] ?? metaObj['left']);
    const y = parseFloat(metaObj['absoluteY'] ?? metaObj['offsetY'] ?? metaObj['y'] ?? metaObj['top']);
    if (!isNaN(x) && !isNaN(y)) {
      return {
        x,
        y,
        pageId: metaObj['pageId'] || metaObj['page'] || metaObj['route'] || metaObj['pageKey'],
        elementId: metaObj['elementId'],
        uniqueSelector: metaObj['uniqueSelector'],
        offsetX: parseFloat(metaObj['offsetX'] || '0'),
        offsetY: parseFloat(metaObj['offsetY'] || '0'),
        tag: metaObj['tag'],
      };
    }
    return null;
  }

  private parseRequestCoordinates(req: any): any | null {
    if (!req) return null;
    let rawMeta = req.metadata;
    if (typeof rawMeta === 'string') {
      try {
        rawMeta = JSON.parse(rawMeta);
      } catch {
        return null;
      }
    }
    if (Array.isArray(rawMeta)) {
      const metaObj: Record<string, any> = {};
      for (const m of rawMeta) {
        if (m && m.key) metaObj[m.key] = m.value;
      }
      return metaObj;
    }
    if (typeof rawMeta === 'object' && rawMeta !== null) {
      return rawMeta;
    }
    return null;
  }

  public parseTaskMetadata(task: UnifiedTaskItem): any | null {
    const raw = task?.rawItem;
    if (!raw) return null;

    if (raw.metadata) {
      let rawMeta = raw.metadata as any;
      if (typeof rawMeta === 'string') {
        try {
          rawMeta = JSON.parse(rawMeta);
        } catch {
          return null;
        }
      }
      if (Array.isArray(rawMeta)) {
        const metaObj: Record<string, string> = {};
        for (const m of rawMeta) {
          if (m && m.key) metaObj[m.key] = m.value;
        }
        return metaObj;
      }
      if (typeof rawMeta === 'object' && rawMeta !== null) {
        return rawMeta;
      }
    }

    return this.parseRequestCoordinates(raw);
  }

  private setupIframeListeners(): void {
    if (this.iframePollInterval) {
      clearInterval(this.iframePollInterval);
    }
    this.iframePollInterval = setInterval(() => {
      try {
        const iframe = this.captureZoneRef?.nativeElement.querySelector(
          'iframe',
        ) as HTMLIFrameElement;
        if (
          iframe &&
          iframe.contentDocument &&
          iframe.contentDocument !== this.boundIframeDoc
        ) {
          this.removeIframeListeners();
          this.boundIframeDoc = iframe.contentDocument;
          this.boundIframeDoc.addEventListener('click', this.boundHandleClick, true);
          this.boundIframeDoc.addEventListener('mousedown', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.addEventListener('pointerdown', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.addEventListener('mouseup', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.addEventListener('pointerup', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.addEventListener('submit', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.addEventListener(
            'mousemove',
            this.boundHandleMouseMove,
            true,
          );
          this.boundIframeDoc.addEventListener(
            'mouseout',
            this.boundHandleMouseOut,
            true,
          );
          this.boundIframeDoc.addEventListener(
            'scroll',
            this.boundHandleScroll,
            true,
          );
          this.boundIframeDoc.addEventListener(
            'wheel',
            this.boundHandleWheel,
            { capture: true, passive: true },
          );
          this.boundIframeDoc.addEventListener(
            'touchmove',
            this.boundHandleTouchMove,
            { capture: true, passive: true },
          );
          if (this.boundIframeDoc.defaultView) {
            this.boundIframeDoc.defaultView.addEventListener('click', this.boundHandleClick, true);
            this.boundIframeDoc.defaultView.addEventListener('mousedown', this.boundHandlePointerEvent, true);
            this.boundIframeDoc.defaultView.addEventListener('pointerdown', this.boundHandlePointerEvent, true);
            this.boundIframeDoc.defaultView.addEventListener('mouseup', this.boundHandlePointerEvent, true);
            this.boundIframeDoc.defaultView.addEventListener('pointerup', this.boundHandlePointerEvent, true);
            this.boundIframeDoc.defaultView.addEventListener('submit', this.boundHandlePointerEvent, true);
            this.boundIframeDoc.defaultView.addEventListener(
              'scroll',
              this.boundHandleScroll,
              true,
            );
            this.boundIframeDoc.defaultView.addEventListener(
              'wheel',
              this.boundHandleWheel,
              { capture: true, passive: true },
            );
            this.boundIframeDoc.defaultView.addEventListener(
              'touchmove',
              this.boundHandleTouchMove,
              { capture: true, passive: true },
            );
          }
          if (this.isAddCommentMode()) {
            this.boundIframeDoc.documentElement.classList.add('rapider-comment-mode');
            if (this.boundIframeDoc.defaultView) {
              (this.boundIframeDoc.defaultView as any).__rapiderCommentModeActive = true;
            }
          }
          try {
            const style = this.boundIframeDoc.createElement('style');
            style.textContent = `
              html.rapider-comment-mode, html.rapider-comment-mode * {
                cursor: crosshair !important;
              }
              .comment-hover-target {
                outline: 2px dashed #ef4444 !important;
                outline-offset: -2px;
                cursor: crosshair !important;
              }
            `;
            this.boundIframeDoc.head?.appendChild(style);
          } catch {}
          this.recalculateBubblePositions();
        } else if (
          this.showBubbles() &&
          this.isSidebarOpen() &&
          this.activeSidebarTab() === 'Tasks' &&
          this.unifiedTasks().length > 0 &&
          Object.keys(this.bubblePositions()).length === 0
        ) {
          this.recalculateBubblePositions();
        }
      } catch {}
    }, 500);
  }

  private removeIframeListeners(): void {
    if (this.boundIframeDoc) {
      this.boundIframeDoc.removeEventListener('click', this.boundHandleClick, true);
      this.boundIframeDoc.removeEventListener('mousedown', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('pointerdown', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('mouseup', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('pointerup', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('submit', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener(
        'mousemove',
        this.boundHandleMouseMove,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'mouseout',
        this.boundHandleMouseOut,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'scroll',
        this.boundHandleScroll,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'wheel',
        this.boundHandleWheel,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'touchmove',
        this.boundHandleTouchMove,
        true,
      );
      if (this.boundIframeDoc.defaultView) {
        this.boundIframeDoc.defaultView.removeEventListener('click', this.boundHandleClick, true);
        this.boundIframeDoc.defaultView.removeEventListener('mousedown', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('pointerdown', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('mouseup', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('pointerup', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('submit', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener(
          'scroll',
          this.boundHandleScroll,
          true,
        );
        this.boundIframeDoc.defaultView.removeEventListener(
          'wheel',
          this.boundHandleWheel,
          true,
        );
        this.boundIframeDoc.defaultView.removeEventListener(
          'touchmove',
          this.boundHandleTouchMove,
          true,
        );
      }
      this.boundIframeDoc = null;
    }
  }

  private getLocalDoc(): Document | null {
    try {
      const iframe = this.captureZoneRef?.nativeElement.querySelector(
        'iframe',
      ) as HTMLIFrameElement;
      if (iframe && iframe.contentDocument) {
        return iframe.contentDocument;
      }
    } catch {}
    if (this.captureZoneRef?.nativeElement) {
      return this.captureZoneRef.nativeElement.ownerDocument;
    }
    return typeof document !== 'undefined' ? document : null;
  }

  public recalculateBubblePositions(): void {
    const doc = this.getLocalDoc();
    const container = this.captureZoneRef?.nativeElement;
    if (!doc || !container) return;

    const canvasRect = container.getBoundingClientRect();

    let iframeTopOffset = 0;
    let iframeLeftOffset = 0;
    const iframe = this.captureZoneRef?.nativeElement.querySelector(
      'iframe',
    ) as HTMLIFrameElement;
    if (iframe && doc === iframe.contentDocument) {
      const iframeRect = iframe.getBoundingClientRect();
      iframeTopOffset = iframeRect.top;
      iframeLeftOffset = iframeRect.left;
    }

    const newPositions: Record<
      string,
      { top: number; left: number; isOrphaned: boolean }
    > = {};

    for (const task of this.unifiedTasks()) {
      if (!this.isTaskOnActivePage(task)) {
        continue;
      }
      const meta = this.parseTaskMetadata(task);
      if (!meta) continue;

      const elementId = meta.elementId;
      let el: HTMLElement | null = null;

      if (elementId) {
        el = doc.querySelector(
          `[data-rapider-node-id="${elementId}"]`,
        ) as HTMLElement;
      }

      if (!el && meta.uniqueSelector) {
        try {
          el = doc.querySelector(meta.uniqueSelector) as HTMLElement;
          if (el && elementId) {
            el.setAttribute('data-rapider-node-id', elementId);
          }
        } catch {}
      }

      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue;

        const offsetY = parseFloat(meta.offsetY ?? meta.y ?? meta.top ?? '0');
        const offsetX = parseFloat(meta.offsetX ?? meta.x ?? meta.left ?? '0');

        const top = Math.round(rect.top + iframeTopOffset + offsetY - canvasRect.top);
        const left = Math.round(rect.left + iframeLeftOffset + offsetX - canvasRect.left);

        if (!isCoordinateWithinCanvasBounds(top, left, canvasRect.width, canvasRect.height)) {
          continue;
        }

        newPositions[task.rawId] = {
          top,
          left,
          isOrphaned: false,
        };
      } else {
        const absX = parseFloat(meta.absoluteX ?? meta.x ?? meta.left);
        const absY = parseFloat(meta.absoluteY ?? meta.y ?? meta.top);
        if (!isNaN(absX) && !isNaN(absY)) {
          const top = Math.round(absY + iframeTopOffset - canvasRect.top);
          const left = Math.round(absX + iframeLeftOffset - canvasRect.left);

          if (!isCoordinateWithinCanvasBounds(top, left, canvasRect.width, canvasRect.height)) {
            continue;
          }

          newPositions[task.rawId] = {
            top,
            left,
            isOrphaned: true,
          };
        }
      }
    }

    this.bubblePositions.set(newPositions);
  }
}
